// kernel/useCommands.ts
import { MAIN_TEXT_MODEL } from '#lib/gptBackend';
import { waitForListMessagePersistence } from '#lib/list/listReferenceContinuation';
import { isListCommand } from '#lib/list/listCommandSurface';
import { getProjectStudioViewMode, isStudioProjectType } from '#lib/studio-project-view';
import { startReviewAfterResearch, useSlidesStore } from '@/components/slides/slidesStore';
import { RESEARCH_COMMANDS } from '#lib/slides/brandResearch';
import { isFactoryPersonaSetupScript, isFactoryReferenceLookupScript, isListContextPreparationScript, requiresListScriptPersistence } from '#lib/list/factoryReferenceLookup';
import { serializeListOutputMentions } from '#lib/list/listOutputMentions';
import { serializeFactoryFolderMentions } from '#lib/list/factoryFolderMentions';
import { useCallback, useMemo, RefObject } from 'react';
import { isSourceSlashCommandText } from '@/components/source/sourceSlashCommands';
import { isPlanSlashCommandText } from '@/components/footfall/planSlashCommands';
import { isFactoryArchetypeCommandText, isFactoryCommandText, parseFactoryArchetypeCommand, parseFactoryCommand } from '@/components/list/listSlashCommands';
import { scriptIsTerminal, budgetProposalScriptError } from '#lib/leasing/terminal-commands';
import { commandAllowedInUnpromptedTurn, unpromptedTurnRefusal } from '#lib/leasing/unprompted-turns';
import { agentCommandRefusal } from '#lib/designer/agentCommandRestriction';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { uploadFileToCloud } from '#lib/azure-client';
import { saveFactoryArchetype } from '../components/list/saveFactoryArchetype';
import { prepareFactorySourceUrls } from '../components/list/factorySourcePreparation';
import { buildFactoryGenerationPlan } from '../components/list/factoryLaunchPlan';
import { getFileExtension } from '#lib/utils';
import { getDefaultParamsForModel } from '#lib/models';
import { COMMAND_DEFINITIONS, commandTimeEstimations } from '@/components/agentCommands';
import { PARALLELIZABLE_COMMANDS } from '@/kernel/useAgents';
import type {
  ChatSession,
  CommandHandler,
  ErrorPayload,
  ImageAttachment,
  KernelHandle,
  Message,
  RuntimeState,
  ScriptInstance,
  AgentMode,
  SessionRuntimeState,
  UserPreferences,
  Workspace,
} from '#types/types';
import { AGENT_LLMS, COMMAND_GROUPS } from '#types/types';
import { resolveProjectIdFromChatSessionContext } from '#lib/leasing/chat-session-project-focus';
import { isEntityFocusSubject } from '#lib/leasing/session-focus';
import { isAnalystViewOpen } from '@/components/tables/tablesStore';
import { MAX_FACTORY_OUTPUTS } from '#lib/list/factoryGenerationLimits';
import { FACTORY_MODEL_ID } from '#lib/list/factoryReferenceTransport';
import { readListFactoryProfile } from '#lib/list/factoryProfile';
import { shouldUnlockIncompatibleStudioModelPin } from '#lib/model-registry';

const FACTORY_DEFAULT_STOCK_INSTRUCTION = 'Create an original image guided by the saved Factory art direction and tone.';
const FACTORY_DEFAULT_SOURCE_INSTRUCTION = 'Create a variation of the supplied source photograph in the saved Factory art direction and tone.';
const isReusableFactoryImageUrl = (url: string): boolean => {
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
};
const NON_BLOCKING_FAILURE_COMMANDS = new Set(['QUERY']);
const shouldContinueAfterFailure = (actionType: string) =>
  NON_BLOCKING_FAILURE_COMMANDS.has(actionType);

// Script ids that are ACTUALLY executing right now (inside runCommand's try/finally).
// This is the source of truth for "is a script really running", which lets the
// running-script guard tell a genuinely-busy session apart from a STALE lock that was
// stranded when an overlapping turn overwrote the session's abortController. Module-level
// so it is shared across every runCommand invocation in the app.
const LIVE_SCRIPT_IDS = new Set<string>();
// askAssistant consults this before refusing a turn: a lock whose script is no longer
// executing (stream killed mid-script, server restart) must not block the session forever.
export const isScriptExecuting = (scriptId: string): boolean => LIVE_SCRIPT_IDS.has(scriptId);
const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
// How long a new script will wait for a genuinely-running one to finish before giving up.
const SCRIPT_LOCK_WAIT_MS = 30000;

// Signature of the script that ran most recently in each session. Used to break a runaway
// autonomous loop where the agent re-emits the byte-identical script every recall (e.g.
// re-opening the same table). An identical CONSECUTIVE script is not auto-recalled, but the
// command still EXECUTES; only the autonomous follow-up is suppressed. Genuine multi-step work
// emits a different command/params each step, so real progress is never cut. This replaces the
// blunt "any script that opens a table can't auto-recall" gate, so opening a table now lets the
// agent keep working (open -> read -> act) while still being immune to re-open storms.
const LAST_SCRIPT_SIG_BY_SESSION = new Map<string, string>();
const MAX_TRACKED_SCRIPT_SIGNATURES = 500; // bound the map so long-lived tabs can't grow it forever
// Order-stable serialization: agent-emitted params can carry the same keys in a different order
// across turns, and a plain JSON.stringify would then read as a DIFFERENT script and let a real
// re-open loop slip past the breaker. Sort keys recursively so identical logical params hash equal.
const stableStringify = (value: unknown): string => {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const obj = value as Record<string, unknown>;
  return `{${Object.keys(obj).sort().map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`;
};
const computeScriptSignature = (s: ScriptInstance): string =>
  s.commands.map((c) => `${c.action_type}:${stableStringify(c.parameters ?? {})}`).join('|');

const isAgentScript = (script: ScriptInstance): boolean =>
  script.isAgentGenerated !== false && !script.isManuallyInvoked;

const lockedStudioModelIsIncompatible = (
  kernel: KernelHandle | null | undefined,
  workspaceId: string | null | undefined,
): boolean => {
  if (!kernel || !workspaceId) return false;
  const projectId = kernel.getWorkspace(workspaceId)?.projectId;
  const projectType = projectId
    ? kernel.getInventory().Project.registry[projectId]?.projectType
    : null;
  return shouldUnlockIncompatibleStudioModelPin(
    kernel.getUserPreferences().lockSelectedModelForAgent === true,
    kernel.getDiffusionModel(),
    projectType,
  );
};

const getCommandRestrictionReason = (
  actionType: string,
  prefs: UserPreferences | undefined,
  inListSelfValidationLoop: boolean,
  inUnpromptedTurn: boolean,
  lockedModelIncompatible = false,
): string | null => {
  // Nobody asked for this turn, the app started it. Read and answer, never write. Checked before
  // the preference rules so no user setting can open it: lib/leasing/unprompted-turns.ts.
  if (inUnpromptedTurn && !commandAllowedInUnpromptedTurn(actionType)) {
    return unpromptedTurnRefusal(actionType);
  }
  if (prefs?.disabledCommands?.includes(actionType)) {
    return agentCommandRefusal(actionType, 'disabled');
  }
  if (
    prefs?.lockSelectedModelForAgent === true &&
    !inListSelfValidationLoop &&
    !lockedModelIncompatible &&
    COMMAND_GROUPS.MODEL_LOCK_RESTRICTED.includes(actionType)
  ) {
    return agentCommandRefusal(actionType, 'model-locked');
  }
  return null;
};

const resolveReferencesWithMap = (value: unknown, idMap: Record<string, string>): unknown => {
  if (Array.isArray(value)) {
    return value.map((entry) => resolveReferencesWithMap(entry, idMap));
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  const record = value as Record<string, unknown>;
  const keys = Object.keys(record);

  if (keys.length === 1 && typeof record.$ref === 'string') {
    return idMap[record.$ref] ?? value;
  }

  return keys.reduce<Record<string, unknown>>((acc, key) => {
    acc[key] = resolveReferencesWithMap(record[key], idMap);
    return acc;
  }, {});
};

interface UseCommandsProps {
  kernel: RefObject<KernelHandle | null>;
}

const resolveSubjectAgentMode = (subject: ChatSession['activeSubject']): AgentMode | null => {
  if (!isEntityFocusSubject(subject)) return null;
  return subject.entityType === 'Project' ? 'director' : 'commander';
};

const patchSessionRuntimeIfAbortControllerOwner = (
  kernelHandle: KernelHandle | null,
  sessionId: string,
  ownerAbortController: AbortController,
  patch: Partial<SessionRuntimeState>
): boolean => {
  if (!kernelHandle) return false;
  const runtime = kernelHandle.readEntityRuntimeState('ChatSession', sessionId);
  if (runtime.abortController !== ownerAbortController) {
    return false;
  }
  kernelHandle.patchEntityRuntimeState('ChatSession', sessionId, patch);
  return true;
};

const useCommands = ({ kernel }: UseCommandsProps) => {
  const runCommand = useCallback(async (script: ScriptInstance, options: {
    workspaceId: string;
    isHeadless?: boolean;
    startingCommandId?: string;
    singleExecution?: boolean;
    onComplete?: (finalWorkspaceId: string) => Promise<void>;
    sessionId?: string;
    claimListRetryAllowance?: () => number;
    listRetrySourceTaskId?: string | null;
    contextOverrides?: {
      idResolutionMap?: Record<string, string>;
    };
  }) => {
    if (
      !kernel.current
    ) {
      const errMsg = '[CommandRunner] Editor context, essential setters, or refs are not available.';
      console.error(errMsg);
      throw new Error(errMsg);
    }

    const {
      workspaceId,
      startingCommandId,
      singleExecution,
      onComplete,
      isHeadless = false,
      sessionId,
      contextOverrides,
    } = options;
    
    const targetSessionId = sessionId || kernel.current.getActiveChatSessionId();
    const isAgentGenerated = isAgentScript(script);
    let reviewRunForScript = targetSessionId && isAgentGenerated ? useSlidesStore.getState().getAgentRun(targetSessionId) : null;
    const sessionForScript = targetSessionId ? kernel.current.getInventory().ChatSession.registry[targetSessionId] : undefined;
    const slidesHomeProjectId = sessionForScript?.creationModule === 'slides' && sessionForScript.creationEntityType === 'Project'
      ? sessionForScript.creationEntityId?.trim() : undefined;
    const owningProjectId = (targetSessionId ? kernel.current.getLastProjectId?.(targetSessionId) : null)
      || kernel.current.getWorkspace(workspaceId)?.projectId;
    const owningProject = owningProjectId ? kernel.current.getInventory().Project.registry[owningProjectId] : undefined;
    const executionViewMode = owningProject?.projectType === 'LIST' ? 'marketing'
      : owningProject && (isStudioProjectType(owningProject.projectType)
        || owningProject.runtime?.projectViewMode === 'studio' || owningProject.runtime?.projectViewMode === 'marketing')
        ? getProjectStudioViewMode(kernel.current, owningProjectId) : script.studioViewMode;
    const reportedGenerationFailures = new Set<string>();
    const listGenerationCohort = new Set<string>();

    // Same rule, same reason: read fresh at each gate rather than caching.
    const isInUnpromptedTurn = (): boolean =>
      targetSessionId
        ? (kernel.current?.readEntityRuntimeState('ChatSession', targetSessionId).unpromptedTurnDepth ?? 0) > 0
        : false;

    if (!targetSessionId) {
        console.warn("[useCommands] No session ID provided for script execution.");
    }
    if (targetSessionId) {
      // Another script may hold this session's lock. Two distinct cases:
      //  (a) GENUINELY running (its id is in LIVE_SCRIPT_IDS). Overlapping turns are normal ,
      //      auto-recall, error loopbacks and "continue" each fire triggerReEvaluation
      //      independently. Serialize instead of dying: wait for it to finish, then take over.
      //  (b) STALE lock (id NOT in LIVE_SCRIPT_IDS). The previous script already finished but
      //      its finally could not clear runningScriptId, because an overlapping turn had
      //      overwritten the session's abortController and the owner-check skipped the reset.
      //      That used to brick the session permanently. Self-heal: clear it and proceed.
      const readLock = () => {
        const rt = kernel.current!.readEntityRuntimeState('ChatSession', targetSessionId);
        return typeof rt.runningScriptId === 'string' ? rt.runningScriptId.trim() : '';
      };
      let lockedBy = readLock();
      if (lockedBy && lockedBy !== script.id) {
        const deadline = Date.now() + SCRIPT_LOCK_WAIT_MS;
        while (
          lockedBy &&
          lockedBy !== script.id &&
          LIVE_SCRIPT_IDS.has(lockedBy) &&
          Date.now() < deadline &&
          !kernel.current!.readEntityRuntimeState('ChatSession', targetSessionId).isStopRequested
        ) {
          await sleep(120);
          lockedBy = readLock();
        }
        if (lockedBy && lockedBy !== script.id) {
          if (LIVE_SCRIPT_IDS.has(lockedBy)) {
            const errMsg = `[CommandRunner] Session "${targetSessionId}" still busy with running script "${lockedBy}" after waiting.`;
            console.warn(errMsg);
            throw new Error(errMsg);
          }
          // Re-confirm the lock STILL points to the same finished script and hasn't been
          // re-claimed by another turn during our wait, THEN clear it. Without this re-check
          // we could clobber a lock a concurrent turn legitimately acquired in the race
          // window, producing overlapping execution on the same session.
          const currentLock = readLock();
          if (currentLock && currentLock !== lockedBy) {
            const errMsg = `[CommandRunner] Session "${targetSessionId}" lock changed to "${currentLock}" during self-heal; yielding to avoid overlap.`;
            console.warn(errMsg);
            throw new Error(errMsg);
          }
          console.warn(
            `[CommandRunner] Clearing stale runningScriptId "${lockedBy}" on session "${targetSessionId}" (that script is no longer executing).`,
          );
          kernel.current.patchEntityRuntimeState('ChatSession', targetSessionId, { runningScriptId: null });
        }
      }
    }

    let wasHaltedByAwait = false;
    let currentWorkspaceContext = workspaceId;

    // Capture the script's signature BEFORE execution mutates command.parameters (resume ids,
    // idResolutionMap, …) so the auto-recall loop-breaker compares like-for-like across runs.
    const scriptSignatureAtStart = computeScriptSignature(script);
    const enforceAgentCommandRestrictions = isAgentGenerated;

    // The lock wait may have yielded to a new review; check immediately before taking ownership.
    if (!isAgentGenerated && targetSessionId && useSlidesStore.getState().getAgentRun(targetSessionId)) {
      const controller = kernel.current.readEntityRuntimeState('ChatSession', targetSessionId).abortController;
      if (controller && !controller.signal.aborted) {
        throw new Error('Stop the current review before running a manual command.');
      }
    }
    if (reviewRunForScript && useSlidesStore.getState().getAgentRun(reviewRunForScript.sessionId)?.id !== reviewRunForScript.id) {
      throw new Error('This review was stopped or superseded.');
    }
    const scriptAbortController = new AbortController();
    let ownsReviewExecution = false;
    const listExecutionScope = requiresListScriptPersistence(script) && targetSessionId ? {
      userId: kernel.current.getUserContext().dbUserId, projectId: kernel.current.getLastProjectId(targetSessionId),
    } : undefined;
    const reviewMayContinue = () => !reviewRunForScript || (!scriptAbortController.signal.aborted &&
      useSlidesStore.getState().getAgentRun(reviewRunForScript.sessionId)?.id === reviewRunForScript.id);
    if (targetSessionId) {
        // isStopRequested is NOT cleared here. It is the user's intent, and a chained agent turn
        // must not be able to erase it: clearing it at every script start meant the stop was wiped
        // by the very next auto-continuation, a fraction of a second after the user pressed it.
        // Reported 2026-09-01 as "il marche pas le bouton arrêt de mon côté", alongside 39
        // generations the user never asked for and could not halt. Only a new USER turn clears it
        // (useChat.submitChatPrompt).
        kernel.current.patchEntityRuntimeState('ChatSession', targetSessionId, {
            abortController: scriptAbortController, // <-- On l'attache à la session
            runningScriptId: script.id,
        });
        // Mark this script as genuinely executing. The matching delete is in finally (and in
        // the startingCommandId error path below). Done synchronously right after claiming the
        // lock so a concurrent runCommand never misreads our fresh lock as stale.
        LIVE_SCRIPT_IDS.add(script.id);
    }

    const updateCurrentMessageState = (currentScript: ScriptInstance) => {
      if (kernel.current && targetSessionId) {
        const activeId = targetSessionId; 
        
        const session = kernel.current?.getInventory().ChatSession.registry[activeId];
        if (!session) return;
        const sessionMessages = kernel.current.getChatSessionMessages(activeId);
        const messageIndex = sessionMessages.findIndex((msg: Message) =>
          msg.scriptsInstances?.some((s: ScriptInstance) => s.id === currentScript.id),
        );
        if (messageIndex === -1) return;

        const newMessagesArray = [...sessionMessages];
        const messageToUpdate = newMessagesArray[messageIndex];
        if (!messageToUpdate) return;

        const targetMessage = { ...messageToUpdate };
        if (targetMessage.scriptsInstances) {
          targetMessage.scriptsInstances = targetMessage.scriptsInstances.map((s: ScriptInstance) => {
            if (s.id === currentScript.id) {
              return { ...currentScript, commands: currentScript.commands.map(cmd => ({ ...cmd })) };
            }
            return s;
          });
        }

        newMessagesArray[messageIndex] = targetMessage;
        kernel.current.updateActiveSessionMessages(() => newMessagesArray, activeId);

        // AND WRITE IT DOWN. Everything above updates the copy in memory, which is why a search
        // looked fine while it was on screen and empty ever after: the stored message keeps the
        // script as it was PARSED, commands pending and resultless, so every later render reported
        // searches that had actually returned sources as "no sources found". Measured 2026-08-16.
        //
        // Fire and forget, deliberately: this is the record catching up with what already happened,
        // so a failed write must not disturb a turn that succeeded. It is skipped for a message with
        // no timeline id, which is a transient one that was never stored to begin with.
        const timelineEventId = typeof targetMessage.timelineEventId === 'string'
          ? targetMessage.timelineEventId.trim()
          : '';
        if (timelineEventId && targetMessage.scriptsInstances && !requiresListScriptPersistence(currentScript)) {
          void fetch(`/api/inventory/timeline-events/${encodeURIComponent(timelineEventId)}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ scriptsInstances: targetMessage.scriptsInstances }),
          }).catch(() => {
            // The turn is unaffected; only the record lags, and the strip now says so rather than
            // claiming an empty result.
          });
        }
      }
    };

    let startIndex = 0;
    if (startingCommandId) {
      const foundIndex = script.commands.findIndex(cmd => cmd.id === startingCommandId);
      if (foundIndex !== -1) {
        startIndex = foundIndex;
      } else {
        const errMsg = `[CommandRunner] Starting command ID "${startingCommandId}" not found in script "${script.label}".`;
        console.warn(errMsg);
        LIVE_SCRIPT_IDS.delete(script.id);
        if (targetSessionId) {
            patchSessionRuntimeIfAbortControllerOwner(
              kernel.current,
              targetSessionId,
              scriptAbortController,
              {
                runningScriptId: null,
                runningCommandId: null,
                runningCommandStartTime: null,
                abortController: null,
              }
            );
        }
        throw new Error(errMsg);
      }
    }

    let idResolutionMap: Record<string, string> = {};
    if (targetSessionId) {
      const sessionRuntime = kernel.current.readEntityRuntimeState('ChatSession', targetSessionId);
      idResolutionMap = { ...(sessionRuntime.idResolutionMap ?? {}) };
    }

    for (let idx = 0; idx < startIndex; idx += 1) {
      const mappingFromHistory = (script.commands[idx]?.parameters?._idResolutionMap ?? {}) as Record<string, string>;
      if (Object.keys(mappingFromHistory).length > 0) {
        idResolutionMap = { ...idResolutionMap, ...mappingFromHistory };
      }
    }

    const overrideIdMap = contextOverrides?.idResolutionMap ?? {};
    if (Object.keys(overrideIdMap).length > 0) {
      idResolutionMap = { ...idResolutionMap, ...overrideIdMap };
    }

    if (targetSessionId) {
      kernel.current.patchEntityRuntimeState('ChatSession', targetSessionId, { idResolutionMap });
    }

    script.commands.forEach((command, idx) => {
      if (!command) return;
      if (idx < startIndex) {
        if (command.status !== 'success') {
          command.status = 'pending';
          command.error = undefined;
        }
        return;
      }
      command.status = 'pending';
      command.error = undefined;
    });

    updateCurrentMessageState(script);

    const isParallelizableCommand = (cmd: ScriptInstance['commands'][number] | undefined) =>
      !!cmd && PARALLELIZABLE_COMMANDS.has(cmd.action_type);

    let shouldAutoRecallSession = false;
    try {
      const proposalError = budgetProposalScriptError(script.commands);
      if (proposalError) {
        for (const command of script.commands) { command.status = 'failed'; command.error = proposalError; }
        updateCurrentMessageState(script);
        throw new Error(proposalError);
      }
      if (executionViewMode === 'marketing') {
        const unsupported = script.commands.slice(startIndex).filter(command => command &&
          (!isListCommand(command.action_type) || command.parameters?.workspaceId));
        if (unsupported.length) {
          for (const command of unsupported) {
            command.status = 'failed';
            command.error = `${command.action_type} is not available in List.`;
          }
          updateCurrentMessageState(script);
          throw new Error('This script contains actions that are not available in List. Use List outputs and explicit references.');
        }
      }
      for (let i = startIndex; i < script.commands.length; ) {
        const command = script.commands[i];
        if (!command) {
          i += 1;
          continue;
        }

        if (!reviewMayContinue()) {
          command.status = 'failed';
          command.error = 'This review was cleared or superseded.';
          updateCurrentMessageState(script);
          break;
        }
        if (targetSessionId && kernel.current.readEntityRuntimeState('ChatSession', targetSessionId).isStopRequested) {
          if (command.status === 'running' || command.status === 'pending') {
            command.status = 'failed';
            command.error = 'Stopped';
          }
          updateCurrentMessageState(script);
          console.log(
            `[CommandRunner] Script "${script.label}" halted by user before command "${command.description || command.action_type}".`,
          );
          break;
        }

        const currentSessionRuntime = targetSessionId
          ? kernel.current.readEntityRuntimeState('ChatSession', targetSessionId)
          : null;
        const commandProjectId = targetSessionId
          ? kernel.current.getLastProjectId?.(targetSessionId)
          : kernel.current.getWorkspace(currentWorkspaceContext)?.projectId;
        const handlerOptions = {
          workspaceId: currentWorkspaceContext,
          projectId: reviewRunForScript?.projectId ?? (slidesHomeProjectId || commandProjectId || undefined),
          studioViewMode: executionViewMode,
          isHeadless,
          isAgentGenerated,
          sessionId: targetSessionId || '',
          claimListRetryAllowance: options.claimListRetryAllowance,
          listRetrySourceTaskId: options.listRetrySourceTaskId,
          allowListNoImageRecovery: true,
          listGenerationCohort,
          abortSignal: currentSessionRuntime?.abortController?.signal
        };

        const research = useSlidesStore.getState().brandResearchRun;
        const researchGuardError = command.action_type === 'SLIDES_END_BRAND_RESEARCH' && script.commands.length !== 1
          ? 'End brand research in its own script, then continue on the next turn.'
          : research?.sessionId === targetSessionId && !RESEARCH_COMMANDS.has(command.action_type)
            ? 'Finish brand research before launching a review.'
            : null;
        if (researchGuardError) {
          command.status = 'failed';
          command.error = researchGuardError;
          updateCurrentMessageState(script);
          break;
        }
        // Chat-started proposals belong to this script and its recalls, not just the image handler.
        if (command.action_type === 'SLIDES_PROPOSE_VISUAL' && !reviewRunForScript && targetSessionId) {
          const proposalProjectId = slidesHomeProjectId || commandProjectId;
          if (proposalProjectId) reviewRunForScript = useSlidesStore.getState().beginProposalRun(proposalProjectId, targetSessionId);
        }
        if (research?.sessionId === targetSessionId) {
          handlerOptions.abortSignal?.addEventListener('abort', () => {
            if (useSlidesStore.getState().brandResearchRun?.id === research.id) useSlidesStore.getState().cancelBrandResearch();
          }, { once: true });
          research.controller.signal.addEventListener('abort', () => {
            const active = useSlidesStore.getState().brandResearchRun;
            if (active && active.id !== research.id) return;
            if (targetSessionId) kernel.current?.patchEntityRuntimeState('ChatSession', targetSessionId, { isStopRequested: true });
            currentSessionRuntime?.abortController?.abort();
          }, { once: true });
        }

        command.parameters = resolveReferencesWithMap(
          command.parameters || {},
          idResolutionMap,
        ) as Record<string, any>;

        const commandLevelIdMap = (command.parameters?._idResolutionMap ?? {}) as Record<string, string>;
        if (Object.keys(commandLevelIdMap).length > 0) {
          idResolutionMap = { ...idResolutionMap, ...commandLevelIdMap };
          if (targetSessionId) {
            kernel.current.patchEntityRuntimeState('ChatSession', targetSessionId, { idResolutionMap });
          }
        }
        
        if (isParallelizableCommand(command)) {
          let chunkEndIndex = i;
          const chunk: ScriptInstance['commands'][number][] = [];
          while (chunkEndIndex < script.commands.length) {
            const candidate = script.commands[chunkEndIndex];
            if (!isParallelizableCommand(candidate)) {
              break;
            }
            if (candidate) {
              chunk.push(candidate);
            }
            chunkEndIndex += 1;
          }

          const chunkDefinitions = chunk.map(cmd => COMMAND_DEFINITIONS[cmd.action_type]);
          const currentPrefs = kernel.current?.getUserPreferences();
          const chunkEligible =
            chunk.length > 0 &&
            chunk.every((cmd, index) => {
              const definition = chunkDefinitions[index];
              if (!definition || !definition.handler) {
                return false;
              }
              if (enforceAgentCommandRestrictions && getCommandRestrictionReason(
                cmd.action_type,
                currentPrefs,
                false,
                isInUnpromptedTurn(),
                lockedStudioModelIsIncompatible(kernel.current, currentWorkspaceContext),
              )) {
                return false;
              }
              if (isHeadless && definition.headlessCompatible === false) {
                return false;
              }
              return true;
            });

          if (chunkEligible) {
            for (const chunkCommand of chunk) {
              chunkCommand.parameters = resolveReferencesWithMap(
                chunkCommand.parameters || {},
                idResolutionMap,
              ) as Record<string, any>;
            }

            for (const chunkCommand of chunk) {
              chunkCommand.status = 'running';
              chunkCommand.error = undefined;
            }

            if (targetSessionId) {
                kernel.current.patchEntityRuntimeState('ChatSession', targetSessionId, {
                    runningCommandId: null, 
                    runningCommandStartTime: null 
                });
            }
            updateCurrentMessageState(script);

            const promises = chunk.map((chunkCommand, index) => {
              const definition = chunkDefinitions[index]!;
              const logDescription = chunkCommand.description || chunkCommand.action_type;

              return (async () => {
                try {
                  const result = await definition.handler(chunkCommand.parameters || {}, kernel, handlerOptions);
                  if (typeof result === 'string' && result !== currentWorkspaceContext) {
                    console.warn(
                      `[CommandRunner] Parallel command attempted to switch workspace context from ${currentWorkspaceContext} to ${result}. Ignoring context change.`,
                    );
                  }

                  if (
                    targetSessionId &&
                    kernel.current?.readEntityRuntimeState('ChatSession', targetSessionId).isStopRequested === true
                  ) {
                    chunkCommand.status = 'failed';
                    chunkCommand.error = 'Stopped';
                    console.log(`[CommandRunner] Command "${logDescription}" halted by user during execution.`);
                    throw new Error(chunkCommand.error);
                  }

                  console.log(`[CommandRunner] Success: ${logDescription}`);
                  chunkCommand.status = 'success';
                } catch (error: any) {
                  if (scriptAbortController.signal.aborted) {
                    chunkCommand.status = 'failed';
                    chunkCommand.error = 'Stopped';
                    throw error;
                  }
                  const message = error?.message || 'Unknown error';
                  if (error?.generationFailureReported === true) reportedGenerationFailures.add(chunkCommand.id);
                  else console.error(`[CommandRunner] Error in command '${logDescription}':`, message);
                  chunkCommand.status = 'failed';
                  chunkCommand.error = message;
                  throw error instanceof Error ? error : new Error(message);
                }
              })();
            });

            const results = await Promise.allSettled(promises);
            updateCurrentMessageState(script);
            if (scriptAbortController.signal.aborted) break;

            const failedIndexes = results.reduce<number[]>((acc, result, resultIndex) => {
              if (result.status === 'rejected') {
                acc.push(resultIndex);
              }
              return acc;
            }, []);

            if (failedIndexes.length > 0) {
              const failedCommands = failedIndexes
                .map(index => chunk[index])
                .filter((candidate): candidate is NonNullable<typeof candidate> => Boolean(candidate));

              const blockingFailedCommand =
                failedCommands.find(cmd => !shouldContinueAfterFailure(cmd.action_type)) || null;
              const failedCommand = blockingFailedCommand ?? failedCommands[0];

              if (!failedCommand) {
                console.error(
                  '[CommandRunner] Parallel command batch reported a failure, but the associated command could not be identified.',
                );
                break;
              }

              if (!blockingFailedCommand) {
                for (const softFailedCommand of failedCommands) {
                  const softFailDescription = softFailedCommand.description || softFailedCommand.action_type;
                  console.warn(
                    `[CommandRunner] Non-blocking command failure for '${softFailDescription}'. Continuing script execution.`,
                  );
                }
                updateCurrentMessageState(script);
                i = chunkEndIndex;
                continue;
              }

              const errorDescription = failedCommand.description || failedCommand.action_type;
              const errorMessage = failedCommand.error || 'Unknown error';

              // A failed batch can contain paid successes or jobs still running.
              // Re-evaluating the original batch can generate them a second time.
              if (failedCommands.some(cmd => cmd.action_type === 'GENERATE_VARIATIONS' || cmd.action_type === 'GENERATE_MEDIA' || cmd.action_type === 'BUILD_FACTORY_PERSONA_PANEL')) {
                if (!reportedGenerationFailures.has(failedCommand.id)) kernel.current?.handleError(errorMessage);
              } else if (script.isManuallyInvoked) {
                kernel.current?.handleError(
                  `Command Failed: ${errorDescription || failedCommand.action_type} - ${errorMessage}`,
                );
              } else if (singleExecution) {
                kernel.current?.handleError(
                  `Step Failed: ${errorDescription || failedCommand.action_type} - ${errorMessage}`,
                );
              } else if (kernel.current?.triggerReEvaluation && targetSessionId && reviewMayContinue() && !kernel.current.readEntityRuntimeState('ChatSession', targetSessionId).isStopRequested) {
                console.log(
                  `[CommandRunner] Command '${errorDescription}' failed. Attempting assistant re-evaluation.`,
                );
                const errorPayload: ErrorPayload = {
                  failedCommandAction: failedCommand.action_type,
                  failedCommandParams: failedCommand.parameters || {},
                  errorMessage,
                  scriptId: script.id,
                  commandId: failedCommand.id,
                  sessionId: targetSessionId || undefined,
                };

                if (workspaceId !== currentWorkspaceContext) {
                  const startWs = kernel.current?.getWorkspace(workspaceId);
                  const failWs = kernel.current?.getWorkspace(currentWorkspaceContext);
                  if (startWs && failWs) {
                    errorPayload.workspaceDrift = {
                      startedIn: { id: startWs.id, name: startWs.name },
                      failedIn: { id: failWs.id, name: failWs.name },
                    };
                  }
                }

                kernel.current
                  .triggerReEvaluation(currentWorkspaceContext, errorPayload)
                  .catch(reEvalError => {
                    console.error('[CommandRunner] Error during triggerReEvaluation call itself:', reEvalError);
                  });
              } else if (targetSessionId && kernel.current.readEntityRuntimeState('ChatSession', targetSessionId).isStopRequested) {
                console.log(
                  `[CommandRunner] Command '${errorDescription}' failed, but user halted script. No assistant re-evaluation.`,
                );
              } else {
                console.warn(
                  `[CommandRunner] Command '${errorDescription}' failed. triggerReEvaluation not available. No assistant re-evaluation.`,
                );
              }

              updateCurrentMessageState(script);
              break;
            }

            i = chunkEndIndex;
            continue;
          }
        }

        if (command.action_type === 'FOLLOW_UP') {
          // Legacy compatibility path: FOLLOW_UP was removed in favor of script-level autoRecall.
          command.status = 'success';
          command.resultSummary = 'Ignored legacy FOLLOW_UP command. Use autoRecall instead.';
          updateCurrentMessageState(script);
          i += 1;
          continue;
        }

        const currentPrefs = kernel.current?.getUserPreferences();
        // The List self-validation loop MUST be free to switch models mid-loop (it iterates
        // on cheap low-res models and renders the final on the user's). That exemption used
        // to live only in SELECT_MODEL's handler, which this gate never reaches: a blocked
        // command is marked failed right here, and `hasFailures` then suppresses autoRecall,
        // so the loop died on turn 1 with no error and no recall. Anyone who picked a model
        // or a resolution chip has the lock on, so this silently disabled looping for them.
        const restrictionReason = enforceAgentCommandRestrictions
          ? getCommandRestrictionReason(
              command.action_type,
              currentPrefs,
              false,
              isInUnpromptedTurn(),
              lockedStudioModelIsIncompatible(kernel.current, currentWorkspaceContext),
            )
          : null;
        if (restrictionReason) {
          const definition = COMMAND_DEFINITIONS[command.action_type];

          if (currentPrefs?.disabledCommands?.includes(command.action_type) && definition?.onDisabled) {
            try {
              await definition.onDisabled(command.parameters || {}, kernel, handlerOptions);
              command.status = 'failed';
              command.error = `Command "${command.action_type}" is disabled. An alternative action was performed.`;
            } catch (e: any) {
              command.status = 'failed';
              command.error = `The disabled handler for "${command.action_type}" failed: ${e.message}`;
            }
          } else {
            const errorMsg = restrictionReason;
            command.status = 'failed';
            command.error = errorMsg;
          }
          updateCurrentMessageState(script);
          i += 1;
          continue;
        }

        const definition = COMMAND_DEFINITIONS[command.action_type];

        if (isHeadless && definition?.headlessCompatible === false) {
          const errorMsg = `Command "${command.action_type}" is not compatible with headless execution.`;
          command.status = 'failed';
          command.error = errorMsg;
          updateCurrentMessageState(script);
          console.error(`[CommandRunner] ${errorMsg}`);
          break;
        }

        command.status = 'running';
        
        if (targetSessionId) {
            kernel.current.patchEntityRuntimeState('ChatSession', targetSessionId, { runningCommandId: command.id });
        }

        const modelId = kernel.current?.getDiffusionModel();
        const estimationKey = command.action_type === 'GENERATE_MEDIA' ? modelId : command.action_type;
        if (estimationKey && commandTimeEstimations[estimationKey]) {
            if (targetSessionId) {
                kernel.current.patchEntityRuntimeState('ChatSession', targetSessionId, { runningCommandStartTime: Date.now() });
            }
        }

        updateCurrentMessageState(script);
        if (definition && definition.handler) {
          try {
            const logDescription = command.description || command.action_type;

            const result = await definition.handler(command.parameters || {}, kernel, handlerOptions);

            if (typeof result === 'string' && result !== currentWorkspaceContext) {
              console.log(
                `[CommandRunner] Script context switched from workspace ${currentWorkspaceContext} to ${result} for subsequent commands.`,
              );
              currentWorkspaceContext = result;
            }

            if (targetSessionId && kernel.current.readEntityRuntimeState('ChatSession', targetSessionId).isStopRequested) {
              command.status = 'failed';
              command.error = 'Stopped';
              console.log(`[CommandRunner] Command "${logDescription}" halted by user during execution.`);
            } else {
              console.log(`[CommandRunner] Success: ${logDescription}`);
              command.status = 'success';
            }

            if (command.action_type.startsWith('AWAIT')) {
              console.log(`[CommandRunner] Halting script execution due to AWAIT command: ${command.action_type}.`);
              updateCurrentMessageState(script);
              wasHaltedByAwait = true;
              break;
            }

          } catch (error: any) {
            if (scriptAbortController.signal.aborted) {
              command.status = 'failed';
              command.error = 'Stopped';
              updateCurrentMessageState(script);
              break;
            }
            const errorDescription = command.description || command.action_type;
            if (error?.generationFailureReported === true) reportedGenerationFailures.add(command.id);
            else console.error(`[CommandRunner] Error in command '${errorDescription}':`, error.message);

            command.status = 'failed';
            command.error = error.message || 'Unknown error';

            if (shouldContinueAfterFailure(command.action_type)) {
              console.warn(
                `[CommandRunner] Non-blocking command failure for '${errorDescription}'. Continuing script execution.`,
              );
              updateCurrentMessageState(script);
              i += 1;
              continue;
            }

            if (command.action_type === 'GENERATE_VARIATIONS' || command.action_type === 'GENERATE_MEDIA' || command.action_type === 'BUILD_FACTORY_PERSONA_PANEL') {
              if (!reportedGenerationFailures.has(command.id)) kernel.current?.handleError(command.error || 'Generation batch failed. Check the task cards before trying again.');
              updateCurrentMessageState(script);
            } else if (script.isManuallyInvoked) {
              kernel.current?.handleError(
                `Command Failed: ${errorDescription || command.action_type} - ${command.error}`,
              );
            } else if (singleExecution) {
              kernel.current?.handleError(
                `Step Failed: ${errorDescription || command.action_type} - ${command.error}`,
              );
              updateCurrentMessageState(script);
            } else if (kernel.current?.triggerReEvaluation && targetSessionId && reviewMayContinue() && !kernel.current.readEntityRuntimeState('ChatSession', targetSessionId).isStopRequested) {
              console.log(`[CommandRunner] Command '${errorDescription}' failed. Attempting assistant re-evaluation.`);
              const errorPayload: ErrorPayload = {
                failedCommandAction: command.action_type,
                failedCommandParams: command.parameters || {},
                errorMessage: command.error || 'Unknown error',
                scriptId: script.id,
                commandId: command.id,
                sessionId: targetSessionId || undefined,
              };

              if (workspaceId !== currentWorkspaceContext) {
                const startWs = kernel.current?.getWorkspace(workspaceId);
                const failWs = kernel.current?.getWorkspace(currentWorkspaceContext);
                if (startWs && failWs) {
                  errorPayload.workspaceDrift = {
                    startedIn: { id: startWs.id, name: startWs.name },
                    failedIn: { id: failWs.id, name: failWs.name },
                  };
                }
              }

              kernel.current
                .triggerReEvaluation(currentWorkspaceContext, errorPayload)
                .catch(reEvalError => {
                  console.error('[CommandRunner] Error during triggerReEvaluation call itself:', reEvalError);
                });
              updateCurrentMessageState(script);
            } else if (targetSessionId && kernel.current.readEntityRuntimeState('ChatSession', targetSessionId).isStopRequested) {
              console.log(
                `[CommandRunner] Command '${errorDescription}' failed, but user halted script. No assistant re-evaluation.`,
              );
              updateCurrentMessageState(script);
            } else {
              console.warn(
                `[CommandRunner] Command '${errorDescription}' failed. triggerReEvaluation not available. No assistant re-evaluation.`,
              );
              updateCurrentMessageState(script);
            }

            break;
          }
        } else {
          const errMsg = `Unknown or unhandled command_type: ${command.action_type}.`;
          console.warn(`[CommandRunner] ${errMsg}`);

          command.status = 'failed';
          command.error = errMsg;

          if (script.isManuallyInvoked) {
            kernel.current?.handleError(`Unknown Command: ${command.action_type}.`);
          } else if (singleExecution) {
            kernel.current?.handleError(`Unknown Step: ${command.action_type}.`);
            updateCurrentMessageState(script);
          } else if (kernel.current?.triggerReEvaluation && targetSessionId && reviewMayContinue() && !kernel.current.readEntityRuntimeState('ChatSession', targetSessionId).isStopRequested) {
            console.log(`[CommandRunner] Unknown command '${command.action_type}'. Attempting assistant re-evaluation.`);
            const errorPayload: ErrorPayload = {
              failedCommandAction: command.action_type,
              failedCommandParams: command.parameters || {},
              errorMessage: errMsg,
              scriptId: script.id,
              commandId: command.id,
              sessionId: targetSessionId || undefined,
            };
            kernel.current
              .triggerReEvaluation(currentWorkspaceContext, errorPayload)
              .catch(reEvalError => {
                console.error(
                  '[CommandRunner] Error during triggerReEvaluation call for unknown command:',
                  reEvalError,
                );
              });
            updateCurrentMessageState(script);
          } else if (targetSessionId && kernel.current.readEntityRuntimeState('ChatSession', targetSessionId).isStopRequested) {
            console.log(
              `[CommandRunner] Unknown command '${command.action_type}', but user halted script. No assistant re-evaluation.`,
            );
            updateCurrentMessageState(script);
          } else {
            console.warn(
              `[CommandRunner] Unknown command '${command.action_type}'. triggerReEvaluation not available. No assistant re-evaluation.`,
            );
            updateCurrentMessageState(script);
          }

          break;
        }

        updateCurrentMessageState(script);

        if (singleExecution && i === startIndex) {
          break;
        }

        i += 1;
      }
      if (targetSessionId && listExecutionScope && kernel.current && !scriptAbortController.signal.aborted) {
        const runtime = kernel.current.readEntityRuntimeState('ChatSession', targetSessionId);
        if (runtime.abortController !== scriptAbortController || runtime.isStopRequested) return;
        try { await waitForListMessagePersistence(runtime.messagePersistence, scriptAbortController.signal); }
        catch (error) { if (scriptAbortController.signal.aborted) return; throw error; }
      }
    } finally {
      LIVE_SCRIPT_IDS.delete(script.id);
      if (script.listGenerationAgreement && (scriptAbortController.signal.aborted
        || (targetSessionId && kernel.current?.readEntityRuntimeState('ChatSession', targetSessionId).isStopRequested))) {
        script.listGenerationAgreement = { ...script.listGenerationAgreement, consumed: true };
      }
      // Always release OUR lock if it still points to us, even when an overlapping turn
      // overwrote the session abortController (which makes the owner-gated reset below skip).
      // Without this, runningScriptId stays stranded and every later script in this session is
      // rejected with "already has running script", permanently bricking the session.
      if (kernel.current && targetSessionId) {
        const latest = kernel.current.readEntityRuntimeState('ChatSession', targetSessionId);
        if (latest.runningScriptId === script.id) {
          const ambiguousGenerationSource = script.commands.some(command => command.action_type === 'GENERATE_VARIATIONS')
            || script.commands.filter(command => command.action_type === 'GENERATE_MEDIA').length > 1;
          kernel.current.patchEntityRuntimeState('ChatSession', targetSessionId, {
            runningScriptId: null,
            ...(ambiguousGenerationSource ? { latestListGenerationTaskIds: [] } : {}),
          });
        }
      }
      updateCurrentMessageState(script);
      const hasFailures = script.commands.some(cmd => cmd.status === 'failed');
      const activeSession =
        targetSessionId && kernel.current
          ? kernel.current.getInventory().ChatSession.registry[targetSessionId]
          : null;
      const resolvedSessionMode =
        activeSession
          ? (resolveSubjectAgentMode(activeSession.activeSubject) ?? 'commander')
          : null;
      const sessionCollaborationMode = activeSession?.collaborationMode ?? null;
      const isAssistedSession = sessionCollaborationMode === 'ASSISTED';
      // The Analyst is a single-mode agent: it always builds a model to completion across
      // as many passes as it needs. The Assisted/Collaborative/Autonomous distinction does
      // NOT apply to it, gating auto-recall on !isAssistedSession is exactly what made it
      // stop after one pass (one flat sheet). So an Analyst session always auto-recalls
      // (still bounded by the byte-identical loop-breaker and the success-loopback cap).
      const isAnalystSession = isAnalystViewOpen();
      // Slides sessions behave like Analyst ones: a review is built to completion
      // across as many passes as it needs (bounded by the loop-breaker), so the
      // Reviewer always auto-recalls, SLIDES_READ_PAGE delivers its page image on
      // the follow-up pass, which a hard stop would strand.
      const isSlidesSession = activeSession?.creationModule === 'slides';
      // Pulse edits then reads its plan's new totals back on the next pass before reporting them.
      const isPulseSession = activeSession?.creationModule === 'pulse';
      // Source sessions are the same shape, and for the same reason. A leasing manager drops one
      // remark on a shortlist ("Electro Depot can't be first, they're too close, review the
      // rubrics") and the Dealer has to work several passes before it has anything to say: read the
      // open shortlist, read the rubric that actually governs it, find which criterion carries
      // proximity, THEN propose weights. Under ASSISTED the loop was cut after the first pass, so
      // the turn ended on "Queried 1 entity" and nothing else, the same single-flat-sheet failure
      // the Analyst had. So a Source session always auto-recalls too.
      // Bounded by the byte-identical loop-breaker and recallLimit (lib/slides/reviewRecall.ts), like every
      // other loop.
      const sourceSubject =
        activeSession && isEntityFocusSubject(activeSession.activeSubject)
          ? activeSession.activeSubject
          : null;
      const isSourceSession =
        activeSession?.creationModule === 'source' ||
        sourceSubject?.focusActionType === 'ORCHESTRATE_SOURCING';
      // A WEB_SEARCH is read-only and useless without the follow-up read + synthesis,
      // so a script that searched the web always continues to a synthesis turn, even in
      // ASSISTED mode (where approval-gated actions like MUTATE still pause). This is what
      // makes browsing feel like Claude: search -> read the pages -> answer with citations,
      // in one flow, instead of stopping on the sources and waiting for the user.
      // READ_DOCTRINE is the same shape: the agent reads a doctrine file precisely to
      // rewrite it next turn (UPDATE_DOCTRINE), stopping after the read strands the flow.
      const scriptHasWebSearch = script.commands.some(
        c => c.action_type === 'WEB_SEARCH' || c.action_type === 'READ_DOCTRINE'
      );
      const canAutoRecallSession =
        isAnalystSession ||
        isSlidesSession ||
        isPulseSession ||
        isSourceSession ||
        ((resolvedSessionMode === 'commander' || resolvedSessionMode === 'director') &&
          (!isAssistedSession || scriptHasWebSearch || isListContextPreparationScript(script.commands) || isFactoryPersonaSetupScript(script.commands)));
      // A failed QUERY is non-blocking: the script carries on past it, so it must not also cancel
      // the wake-up. It did, and the agent sat on its web results in silence until the user typed
      // "?" (/prove on app.fater.ai, 2026-09-26). The next turn reads the error from the history.
      const hasBlockingFailures = script.commands.some(cmd => cmd.status === 'failed' && !shouldContinueAfterFailure(cmd.action_type));
      const isFullyComplete = !hasBlockingFailures && !wasHaltedByAwait;
      // Loop-breaker: if this script is byte-identical to the one that ran immediately before
      // it in this session, do NOT auto-recall, that is a runaway repeat (e.g. the agent
      // re-opening the same table every turn), not real progress. The command already ran;
      // we only skip the autonomous follow-up. Record this run's signature for the next check.
      const previousScriptSignature = targetSessionId
        ? LAST_SCRIPT_SIG_BY_SESSION.get(targetSessionId)
        : undefined;
      const isRepeatOfPreviousScript =
        !!targetSessionId && scriptSignatureAtStart.length > 0 && scriptSignatureAtStart === previousScriptSignature;
      // Only record the signature for runs that can actually auto-recall (dealer/director,
      // non-ASSISTED). Otherwise a manual or ASSISTED run of the same script would poison the map
      // and wrongly suppress a later legitimate autonomous run. Bound the map to cap memory.
      if (isAgentGenerated && targetSessionId && scriptSignatureAtStart.length > 0 && canAutoRecallSession) {
        LAST_SCRIPT_SIG_BY_SESSION.set(targetSessionId, scriptSignatureAtStart);
        if (LAST_SCRIPT_SIG_BY_SESSION.size > MAX_TRACKED_SCRIPT_SIGNATURES) {
          const oldest = LAST_SCRIPT_SIG_BY_SESSION.keys().next().value;
          if (oldest !== undefined) LAST_SCRIPT_SIG_BY_SESSION.delete(oldest);
        }
      }
      // `autoRecall: false` from the agent now means exactly what it says: "your move". It used to
      // be overridden for a Source AUTONOMOUS turn that had auto-accepted an edit, because under
      // the old approval shell the agent wrote it to mean "I have proposed something" and Auto had
      // promised not to stop. There are no proposals any more, so the override is gone with them.
      // A terminal command ends the turn whatever the agent set. The runner's existing guard only
      // catches an IDENTICAL repeat, and on 2026-08-13 the agent saved the same parameter set twice
      // by phrasing the second script differently - two writes, two paragraphs, one request.
      const wantsAnotherPass = script.autoRecall === true && !scriptIsTerminal(script.commands);
      // A turn's words are written before its commands run, so a run that ends on a script never says
      // how it went. Codex ends with a conclusion; so does every agent here: when its last script ends
      // the run cleanly, it is woken ONCE more to write that conclusion, alone. A new user turn re-arms it.
      const conclusionAsked = !!targetSessionId && kernel.current?.readEntityRuntimeState('ChatSession', targetSessionId).runConclusionAsked === true;
      const asksConclusion = !wantsAnotherPass && !conclusionAsked && !hasFailures && !listExecutionScope && !reviewRunForScript
        && script.commands.length > 0 && !scriptIsTerminal(script.commands);
      // A single step run for someone else (the Daimon's run_action) never wakes the module's own
      // assistant: it took the step for a turn of its own, wrote a conclusion, then chained on alone
      // with no stop button on screen (owner, 2026-10-13).
      shouldAutoRecallSession =
        !singleExecution &&
        isAgentGenerated &&
        isFullyComplete &&
        reviewMayContinue() &&
        (wantsAnotherPass || asksConclusion) &&
        !isRepeatOfPreviousScript &&
        Boolean(kernel.current?.triggerReEvaluation) &&
        Boolean(targetSessionId) &&
        canAutoRecallSession;
      if (shouldAutoRecallSession && asksConclusion && targetSessionId) kernel.current?.patchEntityRuntimeState('ChatSession', targetSessionId, { runConclusionAsked: true });

      if (kernel.current && targetSessionId) {
        // Same reason as the script-start patch above: the stop survives the script it stopped,
        // otherwise the finally that tears that script down re-arms the chain it was meant to end.
        const runtimeResetPayload: Record<string, any> = {
          runningCommandId: null,
          runningCommandStartTime: null,
          runningScriptId: null,
          abortController: null,
          ...(shouldAutoRecallSession ? {} : { slidesButtonIntent: null }),
          chatState: (kernel.current.readEntityRuntimeState('ChatSession', targetSessionId).chatState ?? [])
            .filter((step) => step.settled === true),
        };

        ownsReviewExecution = patchSessionRuntimeIfAbortControllerOwner(
          kernel.current,
          targetSessionId,
          scriptAbortController,
          runtimeResetPayload
        );
      }

      console.log('Finished script within workspace ', currentWorkspaceContext);
      if (targetSessionId) {
        console.log('[CommandRunner] Auto-recall decision:', {
          sessionId: targetSessionId,
          scriptId: script.id,
          scriptLabel: script.label,
          scriptAutoRecall: script.autoRecall,
          wantsAnotherPass,
          hasFailures,
          wasHaltedByAwait,
          isFullyComplete,
          resolvedSessionMode,
          sessionCollaborationMode,
          isAssistedSession,
          isRepeatOfPreviousScript,
          hasTriggerReEvaluation: Boolean(kernel.current?.triggerReEvaluation),
          shouldAutoRecallSession,
        });
      }

      if (onComplete && isFullyComplete) {
        await onComplete(currentWorkspaceContext);
      }

      // Error recovery can already own a newer controller before this finally runs.
      if (reviewRunForScript && ownsReviewExecution && !shouldAutoRecallSession) {
        const stopped = scriptAbortController.signal.aborted || kernel.current?.readEntityRuntimeState('ChatSession', reviewRunForScript.sessionId).isStopRequested;
        const finished = isFullyComplete && !singleExecution && !wantsAnotherPass && script.commands.every(cmd => cmd.status === 'success');
        void useSlidesStore.getState().finishAgentRun(reviewRunForScript, stopped ? 'stopped' : hasFailures ? 'failed' : finished ? 'completed' : 'incomplete');
      }
      // A script that ends its chain: a research run opened by Review hands over to the review.
      if (ownsReviewExecution && !shouldAutoRecallSession && targetSessionId) startReviewAfterResearch(kernel.current, targetSessionId);

    }

    if (listExecutionScope && (!ownsReviewExecution || scriptAbortController.signal.aborted || !targetSessionId || !kernel.current
      || kernel.current.readEntityRuntimeState('ChatSession', targetSessionId).isStopRequested
      || kernel.current.getUserContext().dbUserId !== listExecutionScope.userId
      || kernel.current.getActiveChatSessionId() !== targetSessionId
      || kernel.current.getLastProjectId(targetSessionId) !== listExecutionScope.projectId)) return;
    if (shouldAutoRecallSession && reviewMayContinue() && kernel.current) {
        console.log(`[CommandRunner] Script "${script.label}" finished with autoRecall=true. Waking up agent.`);
        kernel.current
          // Pass the session that OWNS this script so the continuation follows it
          // even if the user has navigated away (the active session is no longer it).
          .triggerReEvaluation(currentWorkspaceContext, undefined, targetSessionId ?? undefined)
          .catch((reEvalError) => {
            void useSlidesStore.getState().finishAgentRun(reviewRunForScript, 'failed');
            console.error(
              '[CommandRunner] Error during automatic dealer re-evaluation:',
              reEvalError,
            );
          });
    }
  }, [kernel]);

  const runGeneratedScriptLogic = useCallback(async (
    workspaceId: string,
    scriptToRun: ScriptInstance,
    onComplete?: (finalWorkspaceId: string) => Promise<void>,
    options?: {
      startingCommandId?: string;
      singleExecution?: boolean;
      isHeadless?: boolean;
      sessionId?: string;
      claimListRetryAllowance?: () => number;
      listRetrySourceTaskId?: string | null;
      contextOverrides?: {
        idResolutionMap?: Record<string, string>;
      };
    },
  ) => {
    const reviewRun = isAgentScript(scriptToRun) && options?.sessionId ? useSlidesStore.getState().getAgentRun(options.sessionId) : null;
    const entryController = options?.sessionId ? kernel.current?.readEntityRuntimeState('ChatSession', options.sessionId).abortController : null;
    try {
      if (options?.claimListRetryAllowance && !isFactoryReferenceLookupScript(scriptToRun.commands) && (
        scriptToRun.commands.filter(command => command.action_type === 'GENERATE_MEDIA').length !== 1
        || scriptToRun.commands.some(command => !['GENERATE_MEDIA', 'SELECT_MODEL', 'SET_MODEL_PARAMETER', 'SET_PLAN'].includes(command.action_type))
      )) {
        kernel.current?.handleError('Continuous retry supports one image request at a time. No generation was started.');
        return;
      }
      // A bare runCommand with no session is a direct user action (a toolbar click, a manual
      // slash command), so it is a fresh intent and clears any standing stop. An agent-issued
      // command always carries options.sessionId and must never clear it.
      if (!options?.sessionId && kernel.current) {
          const activeId = kernel.current.getActiveChatSessionId();
          if (activeId) {
              kernel.current.patchEntityRuntimeState('ChatSession', activeId, { isStopRequested: false });
          }
      }

      const runOptions = {
        workspaceId,
        onComplete,
        ...options,
      };

      await runCommand(scriptToRun, runOptions);
    } catch (error) {
      const controller = options?.sessionId ? kernel.current?.readEntityRuntimeState('ChatSession', options.sessionId).abortController : null;
      if (!controller || controller === entryController || controller.signal.aborted) {
        void useSlidesStore.getState().finishAgentRun(reviewRun, 'failed');
      }
      console.error(`[Kernel] Error running generated script "${scriptToRun.label}":`, error);
      kernel.current?.handleError(error instanceof Error ? error.message : `Script "${scriptToRun.label}" failed.`);
    }
  }, [kernel, runCommand]);

  const parseSlashCommand = useCallback((input: string) => {
    const trimmedInput = input.trim();
    if (!trimmedInput) return null;

    const firstSpaceIndex = trimmedInput.indexOf(' ');
    let commandName: string;
    let paramsString: string;

    // DASHES ARE UNDERSCORES HERE. The menu lists actions the way every other command in it is
    // written - lower case, dashed - so "/enrich-brand" is what a user types and ENRICH_BRAND is
    // what the registry is keyed on. Both spellings resolve, so an operator's "/ENRICH_BRAND" and a
    // menu pick are the same command.
    const normaliseCommandName = (raw: string): string => raw.toUpperCase().replace(/-/g, '_');

    if (firstSpaceIndex === -1) {
      commandName = normaliseCommandName(trimmedInput);
      paramsString = '';
    } else {
      commandName = normaliseCommandName(trimmedInput.substring(0, firstSpaceIndex));
      paramsString = trimmedInput.substring(firstSpaceIndex + 1).trim();
    }

    const params: Record<string, any> = {};
    const descriptionParts = [`Manual command: ${commandName}`];
    const arrayParamKeys = ['layerNamesOrIdsToKeepVisible', 'layerNamesOrIds'];

    const paramRegex = /(\w+):([^:]+(?:\s+[^:]+)*?)(?=\s+\w+:|$)/g;
    let match;

    while ((match = paramRegex.exec(paramsString)) !== null) {
      const key = match[1];
      const valueString = match[2]?.trim();

      if (!key || valueString === '') {
        console.warn(`[SlashCommandParser] Malformed parameter (empty key or value for key ${key})`);
        continue;
      }

      descriptionParts.push(`${key}=${valueString}`);

      if (arrayParamKeys.includes(key)) {
        params[key] = valueString
          ?.split(',')
          .map(s => s.trim())
          .filter(s => s !== '');
      } else if (valueString?.toLowerCase() === 'true') {
        params[key] = true;
      } else if (valueString?.toLowerCase() === 'false') {
        params[key] = false;
      } else if (!isNaN(Number(valueString)) && valueString?.trim() !== '') {
        params[key] = Number(valueString);
      } else {
        params[key] = valueString;
      }
    }

    if (paramsString.length > 0 && Object.keys(params).length === 0) {
      console.warn(
        `[SlashCommandParser] Could not parse any parameters from: "${paramsString}". Ensure "key:value" format.`,
      );
    }

    return { commandType: commandName, params, description: descriptionParts.join('; ') };
  }, []);

  const commandHandlers = useMemo<Record<string, CommandHandler>>(
    () => Object.fromEntries(Object.values(COMMAND_DEFINITIONS).map(def => [def.action_type, def.handler])),
    [],
  );
  const execute = useCallback(async (promptToExecute: string, imageUrls: string[] | undefined, options: { agentMode: AgentMode; projectId: string }) => {
    if (!kernel.current) return;
    if (!options || !options.agentMode) {
      throw new Error('useCommands.execute requires an explicit agentMode.');
    }
    const projectId = options.projectId.trim();

    if (kernel.current?.getIsGuestMode()) {
      kernel.current?.openModal('SupportChat');
      return;
    }

    if (!promptToExecute && typeof promptToExecute !== 'string') {
      kernel.current?.handleError('No prompt provided to execute.');
      return;
    }

    try { promptToExecute = serializeFactoryFolderMentions(kernel.current, projectId, serializeListOutputMentions(kernel.current, projectId, promptToExecute)); }
    catch (error) { kernel.current.handleError(error instanceof Error ? error.message : 'Select the output reference again.'); return; }
    const trimmedPrompt = promptToExecute.trim();

    // Our own "/" commands (/refine, /verify-stores, /check-data) are INSTRUCTIONS, not manual
    // action invocations. They must go through a normal agent turn so the goal loop runs and the
    // agent narrates across passes; dispatching them here would run one command and stop, and the
    // lookup below would reject "/refine" as an unknown action type anyway.
    // The action names are passed in so a menu entry can NEVER take an operator's override away
    // from them: `/QUERY entity:Brand` dispatches QUERY whatever the menu happens to list.
    // AN INSTRUCTION ATTACHED TO A COMMAND IS PART OF THE REQUEST, on both halves of the menu.
    //
    // A Source shortcut already worked: it goes through a normal agent turn and the doctrine says to
    // honour the words after it ("/refine but keep La Caverne top three"). An agent action did not.
    // Its parser reads `key:value` pairs by regex and everything else falls on the floor, silently,
    // so "/enrich-brand focus on the French network" ran the bare command as if nothing had been
    // said. That was survivable while these were an operator-only path nobody browsed; they are in
    // the menu now, so the trap is in front of every user.
    //
    // So: a direct dispatch is for a line that is ONLY a command and its parameters. The moment
    // there is prose the agent takes the turn instead - it can run the same command AND respect what
    // was asked, which is what picking a command and typing a sentence after it means.
    const carriesFreeformInstruction = (line: string): boolean => {
      const firstSpace = line.indexOf(' ');
      if (firstSpace === -1) return false;
      const remainder = line.slice(firstSpace + 1).trim();
      if (!remainder) return false;
      // What the parser would consume, removed; anything left is prose it would have discarded.
      const leftover = remainder.replace(/(\w+):([^:]+(?:\s+[^:]+)*?)(?=\s+\w+:|$)/g, '').trim();
      return leftover.length > 0;
    };

    // ONE MODULE, ONE MENU (as List): a Plan conversation refuses Source's commands by name, and Source refuses Plan's.
    const activeSession = kernel.current?.getInventory().ChatSession.registry[kernel.current?.getActiveChatSessionId() ?? ''];
    const isPlanSurface = activeSession?.creationModule === 'pulse';
    const typedCommand = /^\/([a-zA-Z_-]+)/.exec(trimmedPrompt)?.[1] ?? '';
    if (isPlanSurface && (isSourceSlashCommandText(trimmedPrompt, Object.keys(COMMAND_DEFINITIONS)) || ((COMMAND_DEFINITIONS as Record<string, { availableInModes?: readonly string[] }>)[typedCommand.toUpperCase().replace(/-/g, '_')]?.availableInModes?.includes('pulse') === false))) {
      kernel.current?.handleError(`/${typedCommand} is a Source command and does not run in Merch. Type / to see what Merch can do.`);
      return;
    }
    if (!isPlanSurface && isPlanSlashCommandText(trimmedPrompt, Object.keys(COMMAND_DEFINITIONS))) {
      kernel.current?.handleError(`/${typedCommand} is a Merch command. Open a plan in Merch and try again.`);
      return;
    }
    if (
      trimmedPrompt.startsWith('/')
      && !isSourceSlashCommandText(trimmedPrompt, Object.keys(COMMAND_DEFINITIONS))
      && !isPlanSlashCommandText(trimmedPrompt, Object.keys(COMMAND_DEFINITIONS))
      // `/factory` is not an agent action and must not be looked up as one: it is dispatched below,
      // where the dropped images and the chosen model are in hand.
      && !isFactoryCommandText(trimmedPrompt)
      && !carriesFreeformInstruction(trimmedPrompt.substring(1))
    ) {
      const commandString = trimmedPrompt.substring(1);
      const parsedCommand = kernel.current?.parseSlashCommand(commandString);

      if (!parsedCommand) {
        kernel.current?.handleError('Invalid slash command format.');
        return;
      }
      const { commandType, params, description: cmdDescription } = parsedCommand;
      if (!commandHandlers[commandType]) {
        kernel.current?.handleError(`Unknown manual command: /${commandType}`);
        return;
      }

      const manualScript: ScriptInstance = {
        id: `manual-script-${uuidv4()}`,
        label: `Manual: ${commandType}`,
        commands: [
          {
            id: `manual-cmd-${uuidv4()}`,
            action_type: commandType,
            parameters: params,
            description: cmdDescription,
            status: 'pending',
          },
        ],
        autorun: true,
        isAgentGenerated: false,
        isManuallyInvoked: true,
      };

      try {
        const activeId = kernel.current.getActiveChatSessionId();
        await runCommand(manualScript, { 
            workspaceId: kernel.current?.getProjectUIWorkspaceId(projectId) || '',
            isHeadless: false,
            sessionId: activeId || undefined
        });
      } catch (error: any) {
        kernel.current?.handleError(error instanceof Error ? error.message : 'The manual command failed.');
      }
      kernel.current?.setChatPrompt('');
      return;
    }

    const userPreferences = kernel.current?.getUserPreferences();
    const UIWorkspaceId = kernel.current?.getProjectUIWorkspaceId(projectId);
    const modelParams = kernel.current?.getModelParams();
    const agentModeOverride = kernel.current?.getAgentModeOverride();

    const chatTargetModel = userPreferences.assistantModel || MAIN_TEXT_MODEL;
    const isAgentCall = AGENT_LLMS.includes(chatTargetModel);

    // FACTORY, ONE BATCH OF VARIATIONS PER DROPPED IMAGE.
    //
    // BEFORE the agent branch below, and that position is the whole point: in List the composer
    // hands every typed line to the agent, so a factory block further down never ran - measured on
    // the preview, where "/factory 2 variations, same styling" produced two images from the agent's
    // own prompt instead of four from ours. This command is not a request to a model, it is a fan-out
    // that spends once per output, so the composer dispatches it itself and nothing interprets the
    // count.
    //
    // Each variation goes through the ORDINARY single-generation path with that one image as its
    // reference, so credits, snapshots and the List canvas need no special case. The only thing that
    // changes between two outputs of the same image is the seed, because a batch that varies nothing
    // is N copies of one image.
    // Which module's surface is this? The portal sets 'marketing' for a LIST project, and the two
    // command vocabularies are separated on it: List's commands only run here, and Source's only run
    // elsewhere. Hiding a command in the menu is not enough - a name can be typed, pasted, or
    // remembered from another module.
    const isListSurface = kernel.current?.getState?.()?.studioViewMode === 'marketing';
    const factoryProjectWorkspaceIds = kernel.current?.getInventory().Project.registry[projectId]?.workspaceIds;
    const factoryWorkspaceCandidates = Array.isArray(factoryProjectWorkspaceIds)
      ? factoryProjectWorkspaceIds
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
        .map((id) => kernel.current?.getWorkspace(id) ?? null)
        .filter((workspace): workspace is Workspace => workspace !== null && workspace.projectId === projectId)
      : [];
    const stableFactoryWorkspace = factoryWorkspaceCandidates.find((candidate) => readListFactoryProfile(candidate.cameraState))
      ?? factoryWorkspaceCandidates[0]
      ?? (UIWorkspaceId ? kernel.current?.getWorkspace(UIWorkspaceId) : null);
    const factoryScopeWorkspaceId = stableFactoryWorkspace?.id ?? UIWorkspaceId ?? '';

    const archetypeRequest = parseFactoryArchetypeCommand(trimmedPrompt);
    if (isListSurface && isFactoryArchetypeCommandText(trimmedPrompt) && !archetypeRequest) {
      kernel.current?.handleError('Use /archetype: "name" with 1–12 reference images and an optional creative direction, or /archetype edit: @handle a new creative direction.');
      return;
    }

    if (isListSurface && trimmedPrompt.startsWith('/') && !parseFactoryCommand(trimmedPrompt) && !archetypeRequest) {
      const typedName = /^\/([a-zA-Z_-]+)/.exec(trimmedPrompt)?.[1] ?? '';
      const isSourceCommand = isSourceSlashCommandText(trimmedPrompt, Object.keys(COMMAND_DEFINITIONS));
      const isLeasingAction = Object.keys(COMMAND_DEFINITIONS)
        .some((actionType) => actionType.toLowerCase().replace(/_/g, '-') === typedName.toLowerCase());
      if (isSourceCommand || isLeasingAction) {
        // Refused rather than routed: these reach for a campaign, a shortlist or a portfolio that a
        // Studio List session does not have.
        kernel.current?.handleError(
          `/${typedName} is a Source command and does not run in List. Type / to see what List can do.`,
        );
        return;
      }
    }

    if (!isListSurface && (parseFactoryCommand(trimmedPrompt) || isFactoryArchetypeCommandText(trimmedPrompt))) {
      kernel.current?.handleError('/factory and /archetype only run in a List project. Open a List project and try again.');
      return;
    }

    if (archetypeRequest) {
      if (!UIWorkspaceId) {
        kernel.current?.handleError('Open a project workspace before creating an archetype.');
        return;
      }
      const images = [...(kernel.current?.getImagesInChatInput() ?? [])];
      if (!archetypeRequest.handle && (images.length < 1 || images.length > 12)) {
        kernel.current?.handleError('Attach 1–12 reference images to create an archetype. Its creative direction will be derived from those images.');
        return;
      }
      const archetypeProjectId = kernel.current?.getWorkspace(UIWorkspaceId)?.projectId || projectId;
      const archetypeSessionId = kernel.current?.getActiveChatSessionId();
      if (archetypeSessionId) kernel.current?.startThinkingSequence?.(archetypeSessionId);
      try {
        let referenceImages: { imageAssetPath: string }[] | undefined;
        if (!archetypeRequest.handle) {
          const ownerId = kernel.current?.getState().userContext.dbUserId;
          if (!ownerId) throw new Error('Sign in again before uploading archetype references.');
          referenceImages = [];
          for (const image of images) {
            const filename = `factory-archetype-${Date.now()}-${uuidv4()}.${getFileExtension(image.file.name) || 'jpg'}`;
            const imageAssetPath = await uploadFileToCloud(image.file, filename, image.file.type, undefined, ownerId);
            if (!imageAssetPath) throw new Error('An archetype reference could not be uploaded. Your images are still attached; try again.');
            referenceImages.push({ imageAssetPath });
          }
        }
        const archetype = await saveFactoryArchetype({
          workspaceId: factoryScopeWorkspaceId,
          ...(archetypeRequest.handle ? { handle: archetypeRequest.handle } : { name: archetypeRequest.name, referenceImages }),
          description: archetypeRequest.description,
        });
        if (kernel.current?.getActiveChatSessionId() === archetypeSessionId) {
          if (!archetypeRequest.handle) {
            const submittedIds = new Set(images.map((image) => image.id));
            kernel.current?.setImagesInChatInput((current) => current.filter((image) => !submittedIds.has(image.id)));
          }
          if (kernel.current?.getChatPrompt().trim() === trimmedPrompt) kernel.current?.setChatPrompt('');
        }
        if (archetypeSessionId) {
          kernel.current?.setSessionStatus?.(archetypeSessionId, '');
          const now = new Date().toISOString();
          const operationId = uuidv4();
          const messages: Message[] = [
            { clientMessageId: `factory-archetype-${operationId}-command`, role: 'user', content: trimmedPrompt, ...(referenceImages ? { imageUrls: referenceImages.map((image) => image.imageAssetPath) } : {}), timestamp: now },
            { clientMessageId: `factory-archetype-${operationId}-saved`, role: 'assistant', content: archetypeRequest.handle ? `Archetype @${archetype.handle} updated.` : `Archetype @${archetype.handle} created from ${images.length} reference ${images.length === 1 ? 'image' : 'images'}. Creative direction derived from the references.`, timestamp: new Date(Date.parse(now) + 1).toISOString(), agentName: 'Fater' },
          ];
          try {
            kernel.current?.updateActiveSessionMessages((previous) => [...previous, ...messages], archetypeSessionId);
            const receipts = await Promise.allSettled(messages.map((message) => kernel.current?.persistVisibleMessage?.(message, archetypeSessionId)));
            const retainedMessages = kernel.current?.getChatSessionMessages(archetypeSessionId) ?? [];
            if (receipts.some((receipt) => receipt.status === 'rejected') || !messages.every((message) => retainedMessages.some((item) => item.clientMessageId === message.clientMessageId && item.timelineEventId))) {
              throw new Error('The archetype transcript was not retained.');
            }
          } catch {
            // The archetype is already saved: a transcript failure must not retry the mutation.
            toast.warning('Archetype saved, but its confirmation and references could not be retained in chat. You can find them in Factory.');
          }
        }
        kernel.current?.logUserAction(archetypeRequest.handle ? 'Factory Archetype Updated' : 'Factory Archetype Created', { workspaceId: factoryScopeWorkspaceId, name: archetype.name, projectId: archetypeProjectId });
      } catch (error) {
        if (archetypeSessionId) kernel.current?.setSessionStatus?.(archetypeSessionId, '');
        kernel.current?.handleError(error instanceof Error ? error.message : 'Factory could not save this archetype.');
      }
      return;
    }

    const factoryRequest = parseFactoryCommand(trimmedPrompt);
    if (factoryRequest) {
      // Snapshot the attempt before its first await. Composer edits made while uploads are pending
      // belong to the next submission and cannot change this run's source ordering or transcript.
      const factoryPrompt = trimmedPrompt;
      if (!UIWorkspaceId) {
        kernel.current?.handleError('Open a project workspace before using /factory.');
        return;
      }
      const activeFactoryWorkspace = kernel.current?.getWorkspace(UIWorkspaceId);
      const factoryProjectId = activeFactoryWorkspace?.projectId || projectId;
      if (factoryRequest.kind === 'edit') {
        if (!factoryRequest.instruction) {
          kernel.current?.handleError('Add an instruction after /factory edit:.');
          return;
        }
        const factorySessionId = kernel.current?.getActiveChatSessionId();
        if (factorySessionId) kernel.current?.startThinkingSequence?.(factorySessionId);
        let response: Response;
        try {
          response = await fetch('/api/list/factory-direction', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ workspaceId: factoryScopeWorkspaceId, editInstruction: factoryRequest.instruction }),
          });
        } catch {
          if (factorySessionId) kernel.current?.setSessionStatus?.(factorySessionId, '');
          kernel.current?.handleError('Factory direction could not be updated. Your command is still in the composer; try again.');
          return;
        }
        const body = await response.json().catch(() => null) as { cameraState?: unknown; message?: unknown } | null;
        if (!response.ok || !body?.cameraState) {
          if (factorySessionId) kernel.current?.setSessionStatus?.(factorySessionId, '');
          kernel.current?.handleError(
            typeof body?.message === 'string'
              ? body.message
              : 'Factory direction could not be updated. Your command is still in the composer; try again.',
          );
          return;
        }
        kernel.current?.setWorkspace(
          factoryScopeWorkspaceId,
          (workspace) => ({ ...workspace, cameraState: body.cameraState as Workspace['cameraState'] }),
          factoryProjectId ?? undefined,
        );
        kernel.current?.setChatPrompt?.('');
        if (factorySessionId) {
          kernel.current?.setSessionStatus?.(factorySessionId, '');
          const attemptId = uuidv4();
          const messages: Message[] = [
            {
              clientMessageId: `factory-edit-${attemptId}-command`,
              role: 'user',
              content: factoryPrompt,
              timestamp: new Date().toISOString(),
            },
            {
              clientMessageId: `factory-edit-${attemptId}-updated`,
              role: 'assistant',
              content: 'Factory direction updated. No outputs were generated.',
              timestamp: new Date().toISOString(),
              agentName: 'Fater',
            },
          ];
          kernel.current?.updateActiveSessionMessages((previous) => [...previous, ...messages], factorySessionId);
          await Promise.allSettled(messages.map((message) => (
            kernel.current?.persistVisibleMessage?.(message, factorySessionId)
          )));
        }
        kernel.current?.logUserAction('Factory Direction Edited', { workspaceId: factoryScopeWorkspaceId });
        return;
      }

      if (/(^|[\s(])@output:/i.test(factoryRequest.instruction)) {
        kernel.current?.handleError('To use an output as a reference, remove /factory and send your request in List chat with the output mention. No generation was started.');
        return;
      }
      const droppedImages = [...(kernel.current?.getImagesInChatInput() ?? [])];
      const originalPromptImageUrls = Array.isArray(imageUrls)
        ? imageUrls.filter((url): url is string => typeof url === 'string' && url.trim().length > 0)
        : [];
      const promptImageUrls = originalPromptImageUrls.filter(isReusableFactoryImageUrl);
      if (originalPromptImageUrls.length !== promptImageUrls.length) {
        kernel.current?.handleError('One or more original Factory images are no longer available. Upload them again before resending.');
        return;
      }
      // Do not decide whether a saved bank exists from the in-memory workspace. It can lag
      // behind the panel's database write in another tab. The server is the authority and
      // will return the same actionable error only when the persisted bank is genuinely empty.
      const factoryModel = FACTORY_MODEL_ID;
      const factoryModelParams = modelParams?.[factoryModel] ?? getDefaultParamsForModel(factoryModel)?.();
      if (!factoryModelParams) {
        kernel.current?.handleError('Nano Banana is not available for Factory variations right now.');
        return;
      }

      // THE NUMBER IS A TOTAL, NOT A MULTIPLIER. "/factory 3" on three images means three
      // variations, and it briefly meant nine: the images are the material the model works from,
      // all of them together on every output, not three separate batches. Reported by the owner
      // after nine generations came back from a request for three.
      const variations = factoryRequest.variations;
      const factoryRatioLabels = factoryRequest.ratioLabels.length > 0 ? factoryRequest.ratioLabels : [null];
      const totalFactoryOutputs = variations * factoryRatioLabels.length;
      if (totalFactoryOutputs > MAX_FACTORY_OUTPUTS) {
        kernel.current?.handleError(`Factory supports up to ${MAX_FACTORY_OUTPUTS} outputs per command. Use fewer variations or formats.`);
        return;
      }
      const factoryInstruction = factoryRequest.instruction.trim()
        || (droppedImages.length > 0 ? FACTORY_DEFAULT_SOURCE_INSTRUCTION : FACTORY_DEFAULT_STOCK_INSTRUCTION);

      // Factory uses the normal chat transcript for settled milestones. Its only live state is
      // Thinking: the messages below report completed pipeline facts, never invented creative copy.
      const factorySessionId = kernel.current?.getActiveChatSessionId();
      const factoryTelemetryAttemptId = uuidv4();
      const factoryMilestones: Message[] = [];
      const optimisticObjectUrls = droppedImages.map((lockableFile) => URL.createObjectURL(lockableFile.file));
      const optimisticPreviewUrls = droppedImages.length > 0 ? optimisticObjectUrls : promptImageUrls;
      const factoryUserMessageId = `factory-telemetry-${factoryTelemetryAttemptId}-command`;
      const clearFactoryThinking = () => {
        if (!factorySessionId) return;
        kernel.current?.setSessionStatus?.(factorySessionId, '');
      };
      const appendFactoryMilestone = (key: string, content: string) => {
        if (!factorySessionId) return;
        const clientMessageId = `factory-telemetry-${factoryTelemetryAttemptId}-${key}`;
        const message: Message = {
          clientMessageId,
          role: 'assistant',
          content,
          timestamp: new Date().toISOString(),
          agentName: 'Fater',
        };
        factoryMilestones.push(message);
        kernel.current?.updateActiveSessionMessages((prev) => [...prev, message], factorySessionId);
      };
      kernel.current?.setImagesInChatInput([]);
      kernel.current?.setChatPrompt?.('');
      const optimisticUserMessage: Message = {
        clientMessageId: factoryUserMessageId,
        role: 'user',
        content: factoryPrompt,
        timestamp: new Date().toISOString(),
        imageUrls: optimisticPreviewUrls,
      };
      if (factorySessionId) {
        kernel.current?.updateActiveSessionMessages((prev) => [...prev, optimisticUserMessage], factorySessionId);
        clearFactoryThinking();
        kernel.current?.startThinkingSequence?.(factorySessionId);
      }
      // This deliberately re-analyses every Factory batch. The same immutable bank snapshot later
      // supplies the Factory model's bounded native style references.
      const directionRequest = fetch('/api/list/factory-direction', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspaceId: factoryScopeWorkspaceId, factoryInstruction }),
      }).catch(() => null);
      const restoreFactoryInput = () => {
        clearFactoryThinking();
        if (factorySessionId) {
          kernel.current?.updateActiveSessionMessages(
            (prev) => prev.filter((message) =>
              message.clientMessageId !== factoryUserMessageId && !factoryMilestones.includes(message),
            ),
            factorySessionId,
          );
        }
        optimisticObjectUrls.forEach((url) => URL.revokeObjectURL(url));
        if (factorySessionId === kernel.current?.getActiveChatSessionId()) {
          kernel.current?.setImagesInChatInput(droppedImages);
          kernel.current?.setChatPrompt?.(factoryPrompt);
        }
      };

      let durablePromptImageUrls: string[] = [];
      try {
        const activeSessionForUpload = factorySessionId
          ? kernel.current?.getInventory().ChatSession.registry[factorySessionId]
          : null;
        const projectIdFromSession = resolveProjectIdFromChatSessionContext(activeSessionForUpload);
        const scopedProjectId =
          (typeof projectIdFromSession === 'string' && projectIdFromSession.trim().length > 0
            ? projectIdFromSession.trim()
            : projectId) || '';
        const projectOwnerId = scopedProjectId
          ? kernel.current?.getInventory().Project.registry[scopedProjectId]?.userId
          : undefined;

        if (droppedImages.length > 0) {
          durablePromptImageUrls = await prepareFactorySourceUrls(droppedImages, async (lockableFile, index) => {
            const file = lockableFile.file;
            const filename = `${Date.now()}-${index}-${uuidv4()}.${getFileExtension(file.name) || 'bin'}`;
            return uploadFileToCloud(file, filename, file.type, undefined, projectOwnerId);
          }, (url) => kernel.current?.registerCloudAssets?.([url], 'upload'));
        } else if (promptImageUrls.length > 0) {
          durablePromptImageUrls = await prepareFactorySourceUrls(
            promptImageUrls,
            async (url) => url,
            (url) => kernel.current?.registerCloudAssets?.([url], 'upload'),
          );
        }
      } catch (error) {
        console.error('[Factory] source image preparation failed', error);
        restoreFactoryInput();
        kernel.current?.handleError(
          'One or more reference images could not be uploaded. Your prompt and images have been restored; try /factory again.',
        );
        return;
      }

      const durableUserMessage: Message = {
        ...optimisticUserMessage,
        imageUrls: optimisticPreviewUrls.length > 0 ? durablePromptImageUrls : undefined,
      };
      const factoryUserMessageIsVisible = factorySessionId
        ? kernel.current?.getChatSessionMessages(factorySessionId).some(
            (message) => message.clientMessageId === factoryUserMessageId,
          )
        : false;
      if (factorySessionId && factoryUserMessageIsVisible) {
        kernel.current?.updateActiveSessionMessages(
          (prev) => prev.map((message) =>
            message.clientMessageId === factoryUserMessageId ? durableUserMessage : message,
          ),
          factorySessionId,
        );
      }
      optimisticObjectUrls.forEach((url) => URL.revokeObjectURL(url));

      type FactoryDirectionResponse = {
        cameraState?: unknown;
        fingerprint?: unknown;
        analysisId?: unknown;
        factoryProfileRevision?: unknown;
        factoryInstruction?: unknown;
        archetypePrompt?: unknown;
        attachedArchetypes?: unknown;
        listPersonas?: unknown;
        pendingFactoryRunId?: unknown;
        message?: unknown;
      };
      type FactoryReservation = {
        start?: unknown;
        direction?: unknown;
        fingerprint?: unknown;
        analysisId?: unknown;
        nativeReferenceOffsets?: unknown;
        nativeReferenceIdsByVariation?: unknown;
        analyzedAt?: unknown;
        factoryProfileRevision?: unknown;
        factoryInstruction?: unknown;
        archetypePrompt?: unknown;
        attachedArchetypes?: unknown;
        listPersonas?: unknown;
        variationPrompts?: unknown;
        factoryRunId?: unknown;
        cameraState?: unknown;
        message?: unknown;
      };
      let nextDirectionResponse = await directionRequest;
      let diversityReservation: FactoryReservation | null = null;
      let reservedReferenceIdsByVariation: string[][] = [];
      let reservedReferenceOffsets: number[] = [];
      let reservedPrompts: string[] = [];
      let factoryRunId = '';
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const freshDirection = await nextDirectionResponse?.json().catch(() => null) as FactoryDirectionResponse | null;
        if (!nextDirectionResponse?.ok || typeof freshDirection?.fingerprint !== 'string' || typeof freshDirection.analysisId !== 'string') {
          restoreFactoryInput();
          kernel.current?.handleError(
            typeof freshDirection?.message === 'string'
              ? freshDirection.message
              : 'Factory could not read its reference files. No images were generated; try again.',
          );
          return;
        }
        if (freshDirection.cameraState) {
          kernel.current?.setWorkspace(
            UIWorkspaceId,
            (workspace) => ({ ...workspace, cameraState: freshDirection.cameraState as never }),
            factoryProjectId ?? undefined,
          );
        }
        let diversityReservationResponse: Response;
        try {
          diversityReservationResponse = await fetch('/api/list/factory-diversity-slot', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              workspaceId: factoryScopeWorkspaceId,
              count: totalFactoryOutputs,
              directionFingerprint: freshDirection.fingerprint,
              analysisId: freshDirection.analysisId,
              factoryProfileRevision: freshDirection.factoryProfileRevision,
              attachedArchetypes: freshDirection.attachedArchetypes,
              listPersonas: freshDirection.listPersonas,
              factoryInstruction,
              pendingFactoryRunId: freshDirection.pendingFactoryRunId,
              sourceImageUrls: durablePromptImageUrls.filter((url) => url.startsWith('/api/')),
            }),
          });
        } catch {
          restoreFactoryInput();
          kernel.current?.handleError('Factory could not reserve a fresh direction. No images were generated; try again.');
          return;
        }
        const reservation = await diversityReservationResponse.json().catch(() => null) as FactoryReservation | null;
        if (diversityReservationResponse.status === 409 && attempt === 0) {
          nextDirectionResponse = await fetch('/api/list/factory-direction', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ workspaceId: factoryScopeWorkspaceId, factoryInstruction }),
          }).catch(() => null);
          continue;
        }
        if (!diversityReservationResponse.ok || typeof reservation?.start !== 'number' || typeof reservation.direction !== 'string' || reservation.analysisId !== freshDirection.analysisId) {
          restoreFactoryInput();
          kernel.current?.handleError(
            typeof reservation?.message === 'string'
              ? reservation.message
              : 'Factory could not reserve a fresh direction. No images were generated; try again.',
          );
          return;
        }
        const reservationIdsByVariation = Array.isArray(reservation.nativeReferenceIdsByVariation)
          ? reservation.nativeReferenceIdsByVariation.map((ids) => Array.isArray(ids)
            ? ids.filter((id): id is string => typeof id === 'string')
            : [])
          : [];
        const reservationOffsets = Array.isArray(reservation.nativeReferenceOffsets)
          ? reservation.nativeReferenceOffsets.filter((offset): offset is number =>
            typeof offset === 'number' && Number.isInteger(offset) && offset >= 0,
          )
          : [];
        const reservationPrompts = Array.isArray(reservation.variationPrompts)
          ? reservation.variationPrompts.filter((prompt): prompt is string => typeof prompt === 'string' && prompt.length > 0 && prompt.length <= 12_000)
          : [];
        const reservationRunId = typeof reservation.factoryRunId === 'string' && /^[a-f0-9-]{36}$/i.test(reservation.factoryRunId)
          ? reservation.factoryRunId
          : '';
        if (
          reservationIdsByVariation.length !== totalFactoryOutputs
          || reservationOffsets.length !== totalFactoryOutputs
          || reservationPrompts.length !== totalFactoryOutputs
          || !reservationRunId
          || reservationIdsByVariation.some((ids) => ids.length > 14 || new Set(ids).size !== ids.length)
        ) {
          restoreFactoryInput();
          kernel.current?.handleError('Factory could not reserve its saved reference bank. No images were generated; try again.');
          return;
        }
        diversityReservation = reservation;
        reservedReferenceIdsByVariation = reservationIdsByVariation;
        reservedReferenceOffsets = reservationOffsets;
        reservedPrompts = reservationPrompts;
        factoryRunId = reservationRunId;
        break;
      }
      if (!diversityReservation || !factoryRunId || typeof diversityReservation.start !== 'number' || typeof diversityReservation.direction !== 'string') {
        restoreFactoryInput();
        kernel.current?.handleError('Factory could not reserve a fresh direction. No images were generated; try again.');
        return;
      }
      clearFactoryThinking();
      appendFactoryMilestone('references-analyzed', 'Reference files analyzed.');
      appendFactoryMilestone('direction-reserved', 'Direction reserved.');
      if (diversityReservation.cameraState) {
        kernel.current?.setWorkspace(
          UIWorkspaceId,
          (workspace) => ({ ...workspace, cameraState: diversityReservation.cameraState as never }),
          factoryProjectId ?? undefined,
        );
      }

      // The workspace frame is left as-is: with no named ratio it follows the reference image, the
      // right default. When the user DID name a ratio it is an explicit override, applied as the
      // aspect_ratio model param below rather than by reshaping the frame - List generations honor an
      // explicit aspect_ratio over canvas geometry (kernel/useModels.ts), so the ratio takes effect
      // without resizing the generation area under a live run (that reshape once failed as a dev
      // hot-reload artefact, and reshaping an empty frame a second before generating is the fragile
      // path this avoids).
      kernel.current?.prepareWorkspaceForGeneration(UIWorkspaceId);
      // The label is already the param's own format ('16:9'); left unset when the user named no
      // ratio, so useModels keeps deriving it from geometry.
      const factoryLaunchPlan = buildFactoryGenerationPlan(totalFactoryOutputs, factoryRequest.ratioLabels);
      if (factoryLaunchPlan.length !== totalFactoryOutputs) {
        restoreFactoryInput();
        kernel.current?.handleError(
          'Factory could not prepare the requested variations. Your prompt and images have been restored; try /factory again.',
        );
        return;
      }
      const launches: Promise<boolean>[] = [];
      for (const [variation, factoryLaunch] of factoryLaunchPlan.entries()) {
        const reservedReferenceIds = reservedReferenceIdsByVariation[variation];
        const reservedReferenceOffset = reservedReferenceOffsets[variation];
        const reservedPrompt = reservedPrompts[variation];
        if (!reservedReferenceIds || reservedReferenceOffset === undefined || !reservedPrompt) {
          restoreFactoryInput();
          kernel.current?.handleError('Factory could not reserve its saved reference bank. No images were generated; try again.');
          return;
        }
        const factorySelectedParams = { ...factoryModelParams };
        if (!factoryLaunch.ratioLabel) {
          delete (factorySelectedParams as { aspect_ratio?: unknown }).aspect_ratio;
        }
        const factoryOverride: RuntimeState = {
          diffusionModel: factoryModel,
          parameters: {
            ...modelParams,
            [factoryModel]: {
              ...factorySelectedParams,
              prompt: reservedPrompt,
              factoryFeedbackLearning: true,
              factory: true,
              factoryRunId,
              factoryVariationIndex: variation,
              factoryDirectionFingerprint: diversityReservation.fingerprint,
              factoryDirectionAnalysisId: diversityReservation.analysisId,
              factoryDirectionAnalyzedAt: diversityReservation.analyzedAt,
              factoryProfileRevision: diversityReservation.factoryProfileRevision,
              factoryArchetypes: diversityReservation.attachedArchetypes,
              listPersonas: diversityReservation.listPersonas,
              factoryNativeReferenceCount: reservedReferenceIds.length,
              factoryNativeReferenceIds: reservedReferenceIds,
              factoryNativeReferenceOffset: reservedReferenceOffset,
              seed: factoryLaunch.seed,
              add_annotations_as_ref: false,
              enable_google_search: false,
              factoryPreserveSourceAspectRatio: !factoryLaunch.ratioLabel,
              ...(factoryLaunch.ratioLabel ? { aspect_ratio: factoryLaunch.ratioLabel } : {}),
            } as typeof factorySelectedParams & Record<string, unknown>,
          },
          // Factory references are reconstructed from the server reservation. An explicit empty
          // list prevents useModels from inheriting unrelated project attachments.
          referenceImages: [],
        };
        const launch = kernel.current?.runModel(
          factoryScopeWorkspaceId,
          factoryOverride,
          1,
          undefined,
          { factoryRunId },
        )?.then(({ tasks }) => {
          if (tasks.length > 0) return true;
          console.error('[Factory] variation launch returned no task', { variation });
          return false;
        }).catch((error) => {
          console.error('[Factory] variation launch failed', { variation, error });
          return false;
        });
        if (launch) launches.push(launch);
      }

      const launchedOutputCount = (await Promise.all(launches)).filter(Boolean).length;
      if (launchedOutputCount === 0) {
        restoreFactoryInput();
        kernel.current?.handleError('Factory could not launch any outputs. No images were generated; try again.');
        return;
      }
      appendFactoryMilestone(
        'outputs-launched',
        launchedOutputCount === 1 ? '1 output launched.' : `${launchedOutputCount} outputs launched.`,
      );
      if (factorySessionId) {
        const factoryTranscriptMessages = factoryUserMessageIsVisible
          ? [durableUserMessage, ...factoryMilestones]
          : [...factoryMilestones];
        let unsavedFactoryMessages = [...factoryTranscriptMessages];
        for (let persistenceAttempt = 0; persistenceAttempt < 2 && unsavedFactoryMessages.length > 0; persistenceAttempt += 1) {
          await Promise.allSettled(unsavedFactoryMessages.map((message) =>
            kernel.current?.persistVisibleMessage?.(message, factorySessionId),
          ));
          const persistedMessageIds = new Set(
            kernel.current?.getChatSessionMessages(factorySessionId)
              .filter((message) => message.timelineEventId)
              .map((message) => message.clientMessageId)
              .filter((clientMessageId): clientMessageId is string => Boolean(clientMessageId)),
          );
          unsavedFactoryMessages = unsavedFactoryMessages.filter(
            (message) => !persistedMessageIds.has(message.clientMessageId ?? ''),
          );
        }
        if (unsavedFactoryMessages.length > 0) {
          kernel.current?.updateActiveSessionMessages(
            (prev) => prev.filter((message) => !unsavedFactoryMessages.some(
              (unsaved) => unsaved.clientMessageId === message.clientMessageId,
            )),
            factorySessionId,
          );
          kernel.current?.handleError('Factory launched outputs, but could not save the chat update. Check the grid.');
        }
      }
      if (typeof window !== 'undefined' && factoryProjectId) {
        window.dispatchEvent(new CustomEvent('fater:factory-launched', { detail: { projectId: factoryProjectId } }));
      }
      kernel.current?.logUserAction('Factory Batch Launched', {
        images: droppedImages.length,
        variations: launchedOutputCount,
        model_id: factoryModel,
      });
      return;
    }


    if (isAgentCall) {
      const determineAgentMode = (): AgentMode => {
        const activeSessionId = kernel.current?.getActiveChatSessionId();
        if (activeSessionId) {
          const targetSession = kernel.current
            ?.getInventory().ChatSession.registry[activeSessionId];
          if (targetSession?.creationModule === 'slides') return 'slides';
          if (targetSession?.creationModule === 'pulse') return 'pulse';
          const modeFromSubject = resolveSubjectAgentMode(targetSession?.activeSubject ?? null);
          if (modeFromSubject) {
            return modeFromSubject;
          }
          if (options.agentMode === 'commander' || options.agentMode === 'director') {
            return options.agentMode;
          }
        }
        if (options.agentMode === 'commander' || options.agentMode === 'director') {
          return options.agentMode;
        }
        return agentModeOverride === 'commander' || agentModeOverride === 'director'
          ? agentModeOverride
          : 'commander';
      };

      const agentModeForTurn = determineAgentMode();

      if (
        trimmedPrompt.length > 0 ||
        (imageUrls && imageUrls.length > 0) ||
        kernel.current?.getImagesInChatInput().length > 0
      ) {
        try {
          if (kernel.current?.askAssistant) {
            const sessionId = kernel.current.getActiveChatSessionId();
            if (!sessionId) {
                console.warn("Cannot execute command: No active chat session.");
                return;
            }
            await kernel.current?.askAssistant(sessionId, trimmedPrompt, imageUrls, undefined, { agentMode: agentModeForTurn });
          }
        } catch (error) {
          console.error('Error from askAssistant via execute:', error);
        }
      }
      return;
    }

    if (kernel.current?.getSpectacleMode()) {
      kernel.current?.playUISound('GENERATE_START', { volume: 0.4 });
    }

    const imagesInChatInput = kernel.current?.getImagesInChatInput() ?? [];
    const imagesToProcess = imagesInChatInput.map(lf => lf.file);

    if (trimmedPrompt.length === 0 && imagesToProcess.length === 0) {
      return;
    }

    const messageTimestamp = new Date().toISOString();
    const userMessage: Message = {
      content: trimmedPrompt,
      role: 'user',
      timestamp: messageTimestamp,
    };
    const activeId = kernel.current?.getActiveChatSessionId();
    if (activeId) {
      const s = kernel.current?.getInventory().ChatSession.registry[activeId];
      if (s) {
        kernel.current?.updateActiveSessionMessages(
          (prevMessages) => [...prevMessages, userMessage],
          activeId
        );
        kernel.current?.markChatSessionDirty?.(activeId);
      }
    }

    kernel.current?.setChatPrompt('');
    kernel.current?.setImagesInChatInput([]);

    kernel.current?.logUserAction('Direct Generation from Chat', {
      model_id: chatTargetModel,
      prompt: trimmedPrompt,
      prompt_length: trimmedPrompt.length,
      image_count: imagesToProcess.length,
    });

    if (imagesToProcess.length > 0) {
      const uploadAndUpdateMessage = async () => {
        try {
          const activeSession = activeId
            ? kernel.current?.getInventory().ChatSession.registry[activeId]
            : null;
          const projectIdFromSession = resolveProjectIdFromChatSessionContext(activeSession);
          const scopedProjectId =
            (typeof projectIdFromSession === 'string' && projectIdFromSession.trim().length > 0
              ? projectIdFromSession.trim()
              : projectId) || '';
          const projectOwnerId = scopedProjectId
            ? kernel.current?.getInventory().Project.registry[scopedProjectId]?.userId
            : undefined;

          const uploadPromises = imagesInChatInput.map(async (lockableFile): Promise<ImageAttachment | null> => {
            const file = lockableFile.file;
            const isEdited = (file as any).originalFile && (file as any).cropPolygon;
            if (isEdited) {
              const originalFile = (file as any).originalFile as File;
              const polygons = (file as any).cropPolygon as { x: number; y: number }[][];
              const croppedFilename = `${Date.now()}_cropped.${getFileExtension(file.name) || 'bin'}`;
              const originalFilename = `${Date.now()}_original.${getFileExtension(originalFile.name) || 'bin'}`;
              const [croppedUrl, originalUrl] = await Promise.all([
                uploadFileToCloud(file, croppedFilename, file.type, undefined, projectOwnerId),
                uploadFileToCloud(originalFile, originalFilename, originalFile.type, undefined, projectOwnerId),
              ]);
              if (croppedUrl && originalUrl) {
                kernel.current?.registerCloudAssets?.([croppedUrl, originalUrl], 'upload');
                return { url: croppedUrl, originalUrl, polygons };
              }
            } else {
              const filename = `${Date.now()}.${getFileExtension(file.name) || 'bin'}`;
              const url = await uploadFileToCloud(file, filename, file.type, undefined, projectOwnerId);
              if (url) {
                kernel.current?.registerCloudAssets?.([url], 'upload');
                return { url };
              }
            }
            return null;
          });

          const imageAttachments = (await Promise.all(uploadPromises)).filter(
            (att): att is ImageAttachment => !!att,
          );

          if (imageAttachments.length > 0 && kernel.current) {
            if (activeId) {
              kernel.current?.updateActiveSessionMessages(
                (prevMessages) =>
                  prevMessages.map((msg) =>
                    msg.timestamp === messageTimestamp ? { ...msg, images: imageAttachments } : msg
                  ),
                activeId
              );
              kernel.current?.markChatSessionDirty?.(activeId);
            }
          }
        } catch (error) {
          console.error('Failed to upload images for chat message:', error);
        }
      };
      uploadAndUpdateMessage();
    }
    const existingParamsForModel = modelParams[chatTargetModel];
    if (!existingParamsForModel) {
      kernel.current?.handleError(`Parameters for model ${chatTargetModel} not found.`);
      return;
    }

    const runtimeStateOverride: RuntimeState = {
      diffusionModel: chatTargetModel,
      parameters: {
        [chatTargetModel]: {
          ...existingParamsForModel,
          prompt: trimmedPrompt,
        },
      },
    };

    if (imagesToProcess.length > 0) {
      runtimeStateOverride.referenceImages = imagesInChatInput;
    }

    kernel.current?.prepareWorkspaceForGeneration(UIWorkspaceId);
    kernel.current?.runModel(UIWorkspaceId, runtimeStateOverride);
  }, [kernel, runCommand, commandHandlers]);

  const executeSlashCommandDirectly = useCallback(async (commandString: string, projectIdInput: string) => {
    const projectId = projectIdInput.trim();
    if (!projectId) {
      kernel.current?.handleError('executeSlashCommandDirectly requires a projectId.');
      return;
    }
    const individualCommands = commandString
      .split('\n')
      .map(cmd => cmd.trim())
      .filter(cmd => cmd.length > 0);

    for (const command of individualCommands) {
      if (!command.startsWith('/')) {
        kernel.current?.handleError(
          `Invalid shortcut command format in sequence: "${command}". Must start with '/'.`,
        );
        continue;
      }

      const parsedCommand = kernel.current?.parseSlashCommand(command.substring(1));
      if (!parsedCommand) {
        kernel.current?.handleError(`Invalid slash command: ${command}`);
        continue;
      }

      const { commandType, params, description } = parsedCommand;
      const handler = commandHandlers[commandType];

      if (!handler) {
        kernel.current?.handleError(`Unknown command in shortcut: /${commandType}`);
        continue;
      }

      const currentPrefs = kernel.current?.getUserPreferences();
      // Operator slash-commands are direct human intent, not a loop turn, so the lock applies and
      // the unprompted-turn ban does not: the user typed this one themselves.
      const shortcutWorkspaceId = kernel.current?.getProjectUIWorkspaceId(projectId);
      const restrictionReason = getCommandRestrictionReason(
        commandType,
        currentPrefs,
        false,
        false,
        lockedStudioModelIsIncompatible(kernel.current, shortcutWorkspaceId),
      );
      if (restrictionReason) {
        toast.warning(restrictionReason);
        continue;
      }

      try {
        if (kernel.current) {
          await handler(params, kernel, { workspaceId: shortcutWorkspaceId || '', isHeadless: false, isAgentGenerated: false, sessionId: kernel.current.getActiveChatSessionId() || '' });
        }
      } catch (error: any) {
        console.error(`[Kernel] Error executing direct slash command "${description}":`, error);
        kernel.current?.handleError(`Shortcut command failed: ${error.message}`);
        break;
      }
    }
  }, [kernel, commandHandlers]);

  const refreshFactoryDirection = useCallback(async (workspaceId: string, replaceMasterPrompt = true) => {
    const response = await fetch('/api/list/factory-direction', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ workspaceId, replaceMasterPrompt }),
    });
    const body = await response.json() as { cameraState?: unknown; message?: unknown };
    if (!response.ok || !body.cameraState) {
      throw new Error(typeof body.message === 'string' ? body.message : 'Factory direction refresh failed.');
    }
    const currentWorkspace = kernel.current?.getWorkspace(workspaceId);
    if (!currentWorkspace?.projectId) throw new Error('Factory workspace is no longer available.');
    kernel.current?.setWorkspace(
      workspaceId,
      (current) => ({ ...current, cameraState: body.cameraState as Workspace['cameraState'] }),
      currentWorkspace.projectId,
    );
    return body.cameraState as Workspace['cameraState'];
  }, [kernel]);

  return {
    runGeneratedScriptLogic,
    parseSlashCommand,
    execute,
    executeSlashCommandDirectly,
    refreshFactoryDirection,
  };
};

export default useCommands;
